import * as THREE from "three";
import { WebglScene } from "../webgl/scene";
import { InteractiveProjectHandler } from "../configurator/interactiveProjectHandler";
import { WebglFurniture } from "../webgl/furniture";
import { WebglSettings } from "../webgl/settings";
import { CAMERA_STATES, DRAG_STATE, MOUSE_EVENTS, PLAN_WEBGL_MODULE_PATH, WebGLEvents } from "../constants";
import { PhysicsLoader } from 'enable3d';
// @ts-ignore
import async from 'async-es';
import { WebglCamera } from "../webgl/camera";
import { SmartContentHandler } from "../smartContent/smartContentHandler";
import { ArrangementGroup, ArrangementObject, Coating, Commands, ComponentConstants, Entity, Event as SavaneEvent, Events, eventsManager, HttpService, JSONImporter, JSONSerializer, KitchenTool, Listener, math, SavaneCookie, Scene, SceneConstants, World, Floor } from "@rhinov/savane-js";
import { _ASSET_TYPE, createAssetEntity, getAsset, getToken, getUrl, loadPopulatedObjects, switchPopulatedObjects } from "@rhinov/asset-manager-service";

declare let HP_CONFIG: { SMART_URI: string };

interface PlanManagerInstance {
    world: World & { currentScene: Scene & { currentFloor: Floor; arrangementObjects: ArrangementObject[] } };
    state: { actionEditEnded?: () => void };
    sceneIndex: number;
    executeCommand(command: any): void;
    _hideAxo: boolean;
    _hideArrangements: boolean;
    selectedEntities: Entity[];
    selectAll(): void;
}

interface PlanManagerLike {
    getInstance(): PlanManagerInstance;
}

export class WebGLController {

    public displayCurrentFloor: boolean = parseInt(SavaneCookie.getCookie("Rhinov-WebGL-displayCurrentFloor", "0")) === 0 ? false : true;
    public displayPhoto: boolean;

    private _jsonScene: any;
    private _scene: Scene | null = null;
    private _webglScene: WebglScene | null = null;
    private _activeCameraId: number | null = null;
    private _interactiveProjectHandler: InteractiveProjectHandler | null = null;
    private _smartContentHandler: SmartContentHandler | null = null;
    private _loadedEntity: ArrangementObject | Array<ArrangementObject> | null = null;
    private _entity!: string;
    private _entities!: Array<{
        id: string,
        config: any,
        lod: any;
    }>;
    private _config!: string | number;
    private _lod!: string | number;
    private _imgProject!: Array<any>;
    private _photoFromCurrentProjectNb!: number;
    private _leftPanel: boolean = false;
    private _deliverable: any;
    private _deliverableId!: number;
    private _user: any;
    private _controls: boolean = true;
    private _useCameraRatio: boolean = true;
    private _noFocus: boolean = false;
    private _settings: WebglSettings = new WebglSettings();
    private _cameraState: string = CAMERA_STATES.FREE;
    private _displayWarning: boolean = false;
    private _bPressed: boolean = false;
    private _vPressed: boolean = false;
    private _loaded: boolean = false;

    private _delegate: {
        render: CallableFunction,
    };

    private divWebgl!: HTMLElement;
    private _zoom!: number;
    private _thirdGrid!: boolean;
    private _staticHullDatas: any;
    private _dynamicHullDatas: any;
    private _floorGeneratorHullDatas: any;
    private _cameraHeight!: string;
    private _cameraFocal!: string;
    private _photoOpacity!: number;
    private _transformOrigin!: string;
    private _loadedHullCount!: number;
    private _staticHullCanceller!: AbortController;
    private _floorGeneratorCanceller!: AbortController;
    private _dynamicHullCanceller!: AbortController;
    private _videoPreviewNbUnhiddenObjects: any;
    private _videoPreviewCurrentImage: any;
    private _videoPreviewIntervalId: number | undefined;
    private _enable_additionals_afters: boolean;

    private bindedElement!: HTMLElement;
    private mousedownHandler!: (this: HTMLElement, ev: MouseEvent) => any;
    private dblclickHandler!: (this: HTMLElement, ev: MouseEvent) => any;
    private mouseupHandler!: (this: HTMLElement, ev: MouseEvent) => any;
    private mousemoveHandler!: (this: HTMLElement, ev: MouseEvent) => any;
    private mouseleaveHandler!: (this: HTMLElement, ev: MouseEvent) => any;
    private mouseoverHandler!: (this: HTMLElement, ev: MouseEvent) => any;
    private wheelHandler!: (this: HTMLElement, ev: WheelEvent) => any;
    private keydownHandler!: (this: Document, ev: KeyboardEvent) => any;
    private keyupHandler!: (this: Document, ev: KeyboardEvent) => any;
    private currentKeyPressed!: number | null;
    private _mouseOver: any;
    private coatingRotationListener!: Listener;
    private coatingDragListener!: Listener;
    private entityDragListener!: Listener;
    private setFilters!: Listener;
    private assetsUpdated!: Listener;
    private stopEnvUpdate!: Listener;
    private updateEnvs!: Listener;
    private computeCameraShoppingList!: Listener;
    private planStateChangedListener!: Listener;
    private projectLoaded!: Listener;
    private projectUpdated!: Listener;
    private refreshListener!: Listener;
    private hullLoadedListener!: Listener;
    private actionCountChange!: Listener;
    private floorListener!: Listener;
    private reloadScene!: Listener;
    private fieldEditChange!: Listener;
    private resizeListener!: Listener;
    private sceneUpdated!: Listener;
    private fullscreenListener!: Listener;
    private displaySizeChanged!: Listener;
    private updateArrangementCoating!: Listener;
    private floorGeneratorChanged!: Listener;
    private floorGeneratorUpdated!: Listener;
    private frontViewFloorGeneratorChanged!: Listener;
    private hideAxo!: Listener;
    private hideArrangements!: Listener;
    private textFieldListener!: Listener;
    private textFieldEndListener!: Listener;
    private needPopulateListener!: Listener;
    private _transformOriginMouseDown!: number[];
    private _transformOriginOffset!: number[];
    private _dragState: {state: string, data: any, value: any} | null = null;
    private _mouseDownEvent: any;
    private _mouseMoveEvent: any;
    
    private updateFloorGeneratorTimeout: number | null = null;
    private updateDynamicHullTimeout: number | null = null;
    private editRenderCameraCommandTimeout: number | null = null;
    private cocosCameraDragListener!: Listener;
    private cancelFloorGeneratorChanged!: Listener;
    private PlanManager: PlanManagerLike | null;

    // BEGIN: getters and setters

    get jsonScene() : any {
        return this._jsonScene;
    }

    set jsonScene(newValue: any) {
        const oldValue = this._jsonScene;
        this._jsonScene = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (!this._jsonScene) {
                return;
            }

            if (this._scene) {
                this._scene = JSONImporter.importScene(this._jsonScene);

                if (!this._webglScene) {
                    this._init();
                }

                this._getHulls();
                if (this._webglScene) {
                    this._webglScene.updateScene(this._scene);
                }
            }
        }
    }

    get scene() : Scene | null {
        return this._scene;
    }

    set scene(value: Scene) {
        this._scene = value;

        if (!this._webglScene) {
            this._init();
        }

        this._getHulls();
        if (this._webglScene) {
            this._webglScene.updateScene(this._scene);
        }
    }

    get activeCameraId() : number | null {
        return this._activeCameraId;
    }

    set activeCameraId(newValue: number) {
        const oldValue = this._activeCameraId;
        this._activeCameraId = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (newValue !== undefined && this._webglScene) {
                this._webglScene.updateCamera(newValue);
                if (this._interactiveProjectHandler) {
                    this._interactiveProjectHandler.UpdateCamera();
                }
                if (this._loadedEntity) {
                    if (Array.isArray(this._loadedEntity)) {
                        for (let i = 0; i < this._loadedEntity.length; ++i) {
                            this._webglScene.fitCamera(this._loadedEntity[i], i);
                        }
                    } else {
                        this._webglScene.fitCamera(this._loadedEntity, 0);
                    }
                }
            }
        }
    }

    get entity() : string {
        return this._entity;
    }

    set entity(newValue: string) {
        const oldValue = this._entity;
        this._entity = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (!this._webglScene) {
                return;
            }

            this._webglScene.defaultCamera.near = 0.01;
            this._webglScene.defaultCamera.updateProjectionMatrix();

            if (Array.isArray(this._loadedEntity)) {
                for (let i = 0; i < this._loadedEntity.length; ++i) {
                    this._scene!.deleteEntity(this._loadedEntity[i]);
                    this._webglScene.removeEntity(this._loadedEntity[i]);
                }
            } else if (this._loadedEntity) {
                this._scene!.deleteEntity(this._loadedEntity);
                this._webglScene.removeEntity(this._loadedEntity);
            }
            if (newValue !== undefined) {
                this._loadEntity(newValue, this.config, this.lod, undefined);
            }
            this._delegate.render();
        }
    }

    get entities() : Array<{
        id: string,
        config: any,
        lod: any;
    }> {
        return this._entities;
    }

    set entities(newValue: Array<{
        id: string,
        config: any,
        lod: any;
    }>) {
        const oldValue = this._entities;
        this._entities = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (!this._webglScene) {
                return;
            }

            if (this._loadedEntity) {
                if (Array.isArray(this._loadEntity)) {
                    for (let i = 0; i < (this._loadedEntity as Array<Entity>).length; ++i) {
                        this._scene!.deleteEntity((this._loadedEntity as any)[i]);
                        this._webglScene.removeEntity((this._loadedEntity as any)[i]);
                    }
                } else if (this._loadedEntity) {
                    this._scene!.deleteEntity(this._loadedEntity as Entity);
                    this._webglScene.removeEntity(this._loadedEntity as Entity);
                }
            }
            if (newValue && Array.isArray(newValue)) {
                this._webglScene.defaultCamera.near = 0.01;
                this._webglScene.defaultCamera.updateProjectionMatrix();

                const cameras: Array<THREE.PerspectiveCamera> = [];
                for (let i = 0; i < newValue.length; ++i) {
                    const camera = this._webglScene.defaultCamera.clone();
                    this._webglScene.setLayer(camera, i);
                    camera.up.set(0, 0, 1);
                    cameras.push(camera);
                }

                this._webglScene.cameraArray = cameras;
                this._resizeCameras();
                for (let i = 0; i < newValue.length; ++i) {
                    const entity = newValue[i];
                    this._loadEntity(entity.id, entity.config, entity.lod, i);
                }
            }
            this._delegate.render();
        }
    }

    get config() : string | number {
        return this._config;
    }

    set config(newValue: string | number) {
        const oldValue = this._config;
        this._config = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (Array.isArray(this._loadedEntity)) {
                for (let i = 0; i < this._loadedEntity.length; ++i) {
                    this._scene!.deleteEntity(this._loadedEntity[i]);
                    if (this._webglScene) {
                        this._webglScene.removeEntity(this._loadedEntity[i]);
                    }
                }
            } else if (this._loadedEntity) {
                this._scene!.deleteEntity(this._loadedEntity);
                if (this._webglScene) {
                    this._webglScene.removeEntity(this._loadedEntity);
                }
            }

            if (newValue !== undefined && this._loadedEntity) {
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        this._loadEntity(this._loadedEntity[i].objectId, newValue, this.lod, i);
                    }
                } else {
                    this._loadEntity(this._loadedEntity.objectId, newValue, this.lod, undefined);
                }
            }
        }
    }

    get lod() : string | number {
        return this._lod;
    }

    set lod(newValue: string | number) {
        const oldValue = this._lod;
        this._lod = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (Array.isArray(this._loadedEntity)) {
                for (let i = 0; i < this._loadedEntity.length; ++i) {
                    this._scene!.deleteEntity(this._loadedEntity[i]);
                    if (this._webglScene) {
                        this._webglScene.removeEntity(this._loadedEntity[i]);
                    }
                }
            } else if (this._loadedEntity) {
                this._scene!.deleteEntity(this._loadedEntity);
                if (this._webglScene) {
                    this._webglScene.removeEntity(this._loadedEntity);
                }
            }
            if (newValue !== undefined && this._loadedEntity) {
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        this._loadEntity(this._loadedEntity[i].objectId, this.config, newValue, i);
                    }
                } else {
                    this._loadEntity(this._loadedEntity.objectId, this.config, newValue, undefined);
                }
            }
        }
    }

    get imgProject() : Array<any> {
        return this._imgProject;
    }

    set imgProject(newValue: Array<any>) {
        this._imgProject = newValue;
        if (this._webglScene) {
            if (this._webglScene.camera && this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.PhotoRender) {
                this._photoFromCurrentProjectNb = this._webglScene.camera.entity.cameraNb;
                const texture = new THREE.TextureLoader().load(this._getPhotoUrl());
                texture.colorSpace = THREE.SRGBColorSpace;
                this._webglScene.threeScene.background = texture;
                this._webglScene.toggleSky(false);
            }
        }
    }

    get leftPanel() : boolean {
        return this._leftPanel;
    }

    set leftPanel(newValue: boolean) {
        this._leftPanel = newValue;
        this._resize();
    }

    get deliverable() : any {
        return this._deliverable;
    }

    set deliverable(newValue: any) {
        this._deliverable = newValue;
    }

    get deliverableId() : number {
        return this._deliverableId;
    }

    set deliverableId(newValue: number) {
        this._deliverableId = newValue;
    }

    get user() : any {
        return this._user;
    }

    set user(newValue: any) {
        this._user = newValue;
    }

    get controls() : boolean {
        return this._controls;
    }

    set controls(newValue: boolean) {
        const oldValue = this._controls;
        this._controls = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            this._delegate.render();
        }
    }

    get useCameraRatio() : boolean {
        return this._useCameraRatio;
    }

    set useCameraRatio(newValue: boolean) {
        const oldValue = this._useCameraRatio;
        this._useCameraRatio = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (this._webglScene) {
                this._webglScene.useCameraRatio = this._useCameraRatio;
                this._webglScene.updateCamera(this.activeCameraId);
            }
        }
    }

    get noFocus() : boolean {
        return this._noFocus;
    }

    set noFocus(newValue: boolean) {
        this._noFocus = newValue;
    }

    get settings() : WebglSettings {
        return this._settings;
    }

    set settings(newValue: WebglSettings) {
        const oldValue = this._settings;
        this._settings = newValue;

        if (newValue !== undefined && JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            if (oldValue === undefined || oldValue.display !== newValue.display) {
                this.display(newValue.display);
            }
            if (oldValue === undefined || oldValue.meshLevel !== newValue.meshLevel) {
                this.meshLevel = newValue.meshLevel;
            }
            if (oldValue === undefined || oldValue.cellShading !== newValue.cellShading) {
                if (this._webglScene) {
                    this._webglScene.celShadingPass!.enabled = newValue.cellShading;
                    this._webglScene.render();
                }
            }
            this._delegate.render();
        }
    }

    get meshLevel() : number {
        return this._settings.meshLevel;
    }

    set meshLevel(newValue: number) {
        this._settings.meshLevel = newValue;
        if (newValue !== undefined) {
            if (Array.isArray(this._loadedEntity)) {
                for (let i = 0; i < this._loadedEntity.length; ++i) {
                    this._scene!.deleteEntity(this._loadedEntity[i]);
                    if (this._webglScene) {
                        this._webglScene.removeEntity(this._loadedEntity[i]);
                    }
                }
            } else if (this._loadedEntity) {
                this._scene!.deleteEntity(this._loadedEntity);
                if (this._webglScene) {
                    this._webglScene.removeEntity(this._loadedEntity);
                }
            }

            if (newValue !== undefined && this._loadedEntity) {
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        this._loadEntity(this._loadedEntity[i].objectId, this.config, this.lod, i);
                    }
                } else {
                    this._loadEntity(this._loadedEntity.objectId, this.config, this.lod, undefined);
                }
            }
        }
    }

    get loaded() : boolean {
        return this._loaded;
    }

    set loaded(newValue: boolean) {
        const oldValue = this._loaded;
        this._loaded = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            this._delegate.render();
            
            // required for realtime update in interactive project
            if (this._webglScene) {
                this._webglScene.loaded = newValue;
            }
        }
    }

    get zoom() : number {
        return this._zoom;
    }

    set zoom(newValue: number) {
        const oldValue = this._zoom;
        this._zoom = newValue;

        if (newValue !== undefined && JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            this._delegate.render();
        }
    }

    get thirdGrid() : boolean {
        return this._thirdGrid;
    }

    set thirdGrid(newValue: boolean) {
        const oldValue = this._thirdGrid;
        this._thirdGrid = newValue;

        if (newValue !== undefined && JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            this._delegate.render();
        }
    }

    get staticHullDatas() : any {
        return this._staticHullDatas;
    }

    set staticHullDatas(newValue: any) {
        this._staticHullDatas = newValue;
        if (newValue && this._webglScene) {
            this._webglScene.loadStaticHull(newValue, this._scene!);
            this.displayCurrentFloorChanged(this.displayCurrentFloor);
            eventsManager.instance.dispatch(Events.HIDE_AXO);
            this._webglScene.updateEnvs();
            this._webglScene.restoreRoomVisibility();
        }
    }

    get dynamicHullDatas() : any {
        return this._dynamicHullDatas;
    }

    set dynamicHullDatas(newValue: any) {
        this._dynamicHullDatas = newValue;
        if (newValue && this._webglScene) {
            this._webglScene.loadDynamicHull(newValue, this._scene!);
            const floor = (this.displayCurrentFloor && this.PlanManager) ? this.PlanManager.getInstance().world.currentScene.currentFloor : null;
            if (this._webglScene.dynamicHull != null && floor) {
                this._webglScene.dynamicHull.filterFloor(floor);
            }
            eventsManager.instance.dispatch(Events.HIDE_AXO);
            this._webglScene.updateEnvs();
        }
    }

    get floorGeneratorHullDatas() : any {
        return this._floorGeneratorHullDatas;
    }

    set floorGeneratorHullDatas(newValue: any) {
        this._floorGeneratorHullDatas = newValue;
        if (newValue && this._webglScene) {
            this._webglScene.loadFloorGeneratorHull(newValue, this._scene!);
            const floor = (this.displayCurrentFloor && this.PlanManager) ? this.PlanManager.getInstance().world.currentScene.currentFloor : null;
            if (this._webglScene.floorGeneratorHull != null && floor) {
                this._webglScene.floorGeneratorHull.filterFloor(floor);
            }
            eventsManager.instance.dispatch(Events.HIDE_AXO);
            if (this._interactiveProjectHandler && this._webglScene.camera) {
                if (this._webglScene.floorGeneratorHull != null && this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Axonomic) {
                    this._webglScene.floorGeneratorHull.hideCeiling();
                }
            }
            this._webglScene.updateEnvs();
        }
    }

    get cameraHeight() : string {
        return this._cameraHeight;
    }

    set cameraHeight(newValue: string) {
        const oldValue = this._cameraHeight;
        this._cameraHeight = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            this._delegate.render();
        }
    }

    get cameraFocal() : string {
        return this._cameraFocal;
    }

    set cameraFocal(newValue: string) {
        const oldValue = this._cameraFocal;
        this._cameraFocal = newValue;

        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            this._delegate.render();
        }
    }

    get photoOpacity() : number {
        return this._photoOpacity;
    }

    set photoOpacity(newValue: number) {
        const oldValue = this._photoOpacity;
        this._photoOpacity = newValue;

        if (oldValue !== newValue) {
            this._delegate.render();
        }
    }

    get transformOrigin() : string {
        return this._transformOrigin;
    }

    set transformOrigin(newValue: string) {
        const oldValue = this._transformOrigin;
        this._transformOrigin = newValue;

        if (oldValue !== newValue) {
            this._delegate.render();
        }
    }

    // END: getters and setters

    _updateHulls(event?: MouseEvent) : void {
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }

        if (this.PlanManager && (this.PlanManager.getInstance().state as any).actionEditEnded !== undefined) {
            (this.PlanManager.getInstance().state as any).actionEditEnded();
            eventsManager.instance.dispatch(Events.HIDE_TULIP, null);
        }

        this._getHulls();
    }

    async _getStaticHull() : Promise<void> {
        if (!this._scene || !this._webglScene) {
            return;
        }

        if (this._staticHullCanceller && this._staticHullCanceller.signal.aborted === false) {
            this._staticHullCanceller.abort();
        }
        this._staticHullCanceller = new AbortController();

        if (this._webglScene !== null) {
            this._webglScene.cleanUpStaticHull();
        }
        const SMART_URI = (typeof HP_CONFIG !== 'undefined') ? HP_CONFIG.SMART_URI : 'https://smart.rhinovplanner.com/';

        const filtered_rooms = this._webglScene.visibleRooms.join();
        const content = JSON.stringify({
            id: this.deliverableId ? Number(this.deliverableId) : -1,
            index: (this.PlanManager ? this.PlanManager.getInstance().sceneIndex : 0),
            filtered_rooms: filtered_rooms ? filtered_rooms : null,
            rhinov_format: KitchenTool.VersionHandler.toHullFormat(JSONSerializer.serializeEntityWithFilters(this._scene,
                [SceneConstants.EntityType.FunctionalityChip, SceneConstants.EntityType.Light,
                SceneConstants.EntityType.RenderCamera, SceneConstants.EntityType.ArrangementZone, SceneConstants.EntityType.SketchBlock,
                SceneConstants.EntityType.WorkTop, SceneConstants.EntityType.UserPicture,
                SceneConstants.EntityType.Sun],
                [ComponentConstants.ComponentType.CoatingArea,
                ComponentConstants.ComponentType.TemplateImage], true))
        });

        let riserPath = SMART_URI + 'rise/unreal/static/';
        if (this.settings.hullbin) {
            riserPath = SMART_URI + 'rise/unreal/static/bin/';
        }

        //  Static hull
        try {
            let response = await fetch(riserPath, {
                method: "POST",
                signal: this._staticHullCanceller.signal,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: content,
            });

            if (!this.settings.hullbin) {
                let data = await response.json();
                this.staticHullDatas = data.obj;
                if (this._webglScene) {
                    this._webglScene.updateSettings();
                    this._webglScene.updateEnvs();
                }
                this._loadedHullCount += 1;
                if (this._loadedHullCount >= 3) {
                    eventsManager.instance.dispatch(Events.HULL_LOADED);
                    if (this._webglScene) {
                        this._webglScene.restoreRoomVisibility();
                    }
                }
            } else {
                let data = await response.text();
                this.staticHullDatas = data;
                if (this._webglScene) {
                    this._webglScene.updateSettings();
                    this._webglScene.updateEnvs();
                }
                this._loadedHullCount += 1;
                if (this._loadedHullCount >= 3) {
                    eventsManager.instance.dispatch(Events.HULL_LOADED);
                    if (this._webglScene) {
                        this._webglScene.restoreRoomVisibility();
                    }
                }
            }
        } catch {}
    }

    async _getFloorGeneratorHull() : Promise<void> {
        if (!this._scene || !this._webglScene) {
            return;
        }

        if (this._floorGeneratorCanceller && this._floorGeneratorCanceller.signal.aborted === false) {
            this._floorGeneratorCanceller.abort();
        }
        this._floorGeneratorCanceller = new AbortController();

        if (this._webglScene !== null) {
            this._webglScene.cleanUpFloorGeneratorHull();
        }

        const SMART_URI = (typeof HP_CONFIG !== 'undefined') ? HP_CONFIG.SMART_URI : 'https://smart.rhinovplanner.com/';

        const filtered_rooms = this._webglScene.visibleRooms.join();
        const content = JSON.stringify({
            id: this.deliverableId ? Number(this.deliverableId) : -1,
            index: (this.PlanManager ? this.PlanManager.getInstance().sceneIndex : 0),
            filtered_rooms: filtered_rooms ? filtered_rooms : null,
            rhinov_format: KitchenTool.VersionHandler.toHullFormat(JSONSerializer.serializeEntityWithFilters(this._scene,
                [SceneConstants.EntityType.FunctionalityChip, SceneConstants.EntityType.Light,
                SceneConstants.EntityType.RenderCamera, SceneConstants.EntityType.ArrangementZone, SceneConstants.EntityType.SketchBlock,
                SceneConstants.EntityType.WorkTop, SceneConstants.EntityType.UserPicture,
                SceneConstants.EntityType.Sun],
                [ComponentConstants.ComponentType.TemplateImage], true))
        });

        let riserPath = SMART_URI + 'rise/unreal/floorgenerator/';
        if (this.settings.hullbin) {
            riserPath = SMART_URI + 'rise/unreal/floorgenerator/bin/';
        }

        try {
            let response = await fetch(riserPath, {
                method: "POST",
                signal: this._floorGeneratorCanceller.signal,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: content
            });

            if (!this.settings.hullbin) {
                let data = await response.json();
                this.floorGeneratorHullDatas = data.obj;
                this._loadedHullCount += 1;
                if (this._loadedHullCount >= 3) {
                    eventsManager.instance.dispatch(Events.HULL_LOADED);
                    if (this._webglScene) {
                        this._webglScene.restoreRoomVisibility();
                    }
                }
            } else {
                let data = await response.text();
                this.floorGeneratorHullDatas = data;
                this._loadedHullCount += 1;
                if (this._loadedHullCount >= 3) {
                    eventsManager.instance.dispatch(Events.HULL_LOADED);
                    if (this._webglScene) {
                        this._webglScene.restoreRoomVisibility();
                    }
                }
            }
        } catch {}
    }

    async _getDynamicHull() : Promise<void> {
        if (!this._scene || !this._webglScene) {
            return;
        }

        if (this._dynamicHullCanceller && this._dynamicHullCanceller.signal.aborted === false) {
            this._dynamicHullCanceller.abort();
        }
        this._dynamicHullCanceller = new AbortController();

        if (this._webglScene !== null) {
            this._webglScene.cleanUpDynamicHull();
        }

        const SMART_URI = (typeof HP_CONFIG !== 'undefined') ? HP_CONFIG.SMART_URI : 'https://smart.rhinovplanner.com/';

        const filtered_rooms = this._webglScene.visibleRooms.join();
        const content = JSON.stringify({
            id: this.deliverableId ? Number(this.deliverableId) : -1,
            index: (this.PlanManager ? this.PlanManager.getInstance().sceneIndex : 0),
            filtered_rooms: filtered_rooms ? filtered_rooms : null,
            rhinov_format: KitchenTool.VersionHandler.toHullFormat(JSONSerializer.serializeEntityWithFilters(this._scene,
                [SceneConstants.EntityType.Staircase, SceneConstants.EntityType.Light,
                SceneConstants.EntityType.RenderCamera, SceneConstants.EntityType.UserPicture,
                SceneConstants.EntityType.Sun],
                [ComponentConstants.ComponentType.Functionality, ComponentConstants.ComponentType.TemplateImage], true))
        });

        let riserPath = SMART_URI + 'rise/unreal/dynamic/';
        if (this.settings.hullbin) {
            riserPath = SMART_URI + 'rise/unreal/dynamic/bin/';
        }
 
        try {
            let response = await fetch(riserPath, {
                method: "POST",
                signal: this._dynamicHullCanceller.signal,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: content
            });

            if (!this.settings.hullbin) {
                let data = await response.json();
                this.dynamicHullDatas = data.obj;
                this._loadedHullCount += 1;
                if (this._loadedHullCount >= 3) {
                    eventsManager.instance.dispatch(Events.HULL_LOADED);
                    if (this._webglScene) {
                        this._webglScene.restoreRoomVisibility();
                    }
                }
            } else {
                let data = await response.text();
                this.dynamicHullDatas = data;
                this._loadedHullCount += 1;
                if (this._loadedHullCount >= 3) {
                    eventsManager.instance.dispatch(Events.HULL_LOADED);
                    if (this._webglScene) {
                        this._webglScene.restoreRoomVisibility();
                    }
                }
            }
        } catch {}
    }

    _getHulls() : void {
        this._loadedHullCount = 0;
        this._getStaticHull();
        this._getDynamicHull();
        this._getFloorGeneratorHull();
    }

    displayHeight() : boolean {
        if (this._webglScene) {
            return this._webglScene.displayHeight;
        }

        return false;
    }

    displayCameraParameters() : boolean {
        if (this.settings && (this.settings.interactiveProject || this.settings.smartContent)) {
            return false;
        }

        if (this._webglScene) {
            return !!this._webglScene.camera;
        }

        return false;
    }

    //*************CAMERAS***********
    //Set to cocos camera
    _setCocosCamera() : void {
        if (!this._webglScene) return;

        const webglCanvas = this._webglScene.renderer.domElement;

        // Get the current dimensions of the elements
        const heightBuffer = webglCanvas.offsetHeight;
        const widthBuffer = webglCanvas.offsetWidth;

        // Set the camera state
        this._cameraState = CAMERA_STATES.COCOS;

        // Update the camera
        this._webglScene.updateCamera(null);

        // Set the dimensions of the elements
        webglCanvas.style.height = heightBuffer + 'px';
        webglCanvas.style.width = widthBuffer + 'px';

        this.divWebgl.style.height = heightBuffer + 'px';
        this.divWebgl.style.width = widthBuffer + 'px';
    }

    _updateSettings(): void {
        // Assign it to the rhinov cookie as a string
        SavaneCookie.setCookie("Rhinov-PlanWebGL-Settings", JSON.stringify(this.settings));

        if (this._webglScene) {
            this._webglScene.settings = this.settings;
            // Quality has changed, refresh the view to apply the change
            this._webglScene.updateSettings();
            if (this._loadedEntity) {
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        this._webglScene.fitCamera(this._loadedEntity[i], i);
                    }
                } else {
                    this._webglScene.fitCamera(this._loadedEntity, 0);
                }
            }
        }
    }

    _setFreeCamera($event: MouseEvent) : void {
        $event.stopPropagation();
        this._cameraState = CAMERA_STATES.FREE;
        if (this._webglScene) {
            this._webglScene.updateCamera(null);
        }
        eventsManager.instance.dispatch(Events.CAMERA_RESIZED);
    }

    _gizmoSpace() : string {
        if (this._webglScene) {
            return this._webglScene.gizmo.space;
        }

        return 'world';
    }

    _gizmoEnabled() : boolean {
        if (this._webglScene) {
            return this._webglScene.gizmo['enabled'];
        }

        return false;
    }

    _changeGizmoSpace(event: MouseEvent | KeyboardEvent) : void {
        event.preventDefault();
        event.stopPropagation();
        if (this._webglScene) {
            if (this._webglScene.gizmo.mode === 'scale') {
                return;
            }
            if (this._webglScene.gizmo.space === 'world') {
                this._webglScene.gizmo.space = 'local';
            } else {
                this._webglScene.gizmo.space = 'world';
            }
        }
    }

    _togglePhysics(event: MouseEvent | KeyboardEvent) : void {
        event.preventDefault();
        event.stopPropagation();
        if (this._webglScene) {
            this._webglScene.physicsEnabled = !this._webglScene.physicsEnabled;
        }
    }

    _physicEnabled() : boolean {
        if (this._webglScene) {
            return this._webglScene.physicsEnabled;
        }

        return false;
    }

    _fullScreen($event: MouseEvent) : void {
        if (!this._webglScene) return;
        
        $event.stopPropagation();
        const canvas = this._webglScene.renderer.domElement;

        if (this.divWebgl.offsetWidth == document.documentElement.clientWidth) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
            this.divWebgl.style.height = 0 + 'px';
            canvas.style.height = 0 + 'px';
        } else {
            this.divWebgl.style.height = "100%";
            this.divWebgl.style.width = "100%";
            canvas.style.height = "100%";
            canvas.style.width = "100%";
            this.divWebgl.requestFullscreen();
        }
        this._resize();
    }

    _init() : void {
        this._webglScene = new WebglScene(this.divWebgl, this._scene, this.settings, this.controls, this.useCameraRatio, this.leftPanel, this.PlanManager);
        if (this.settings.interactiveProject) {
            this._interactiveProjectHandler = new InteractiveProjectHandler(this.divWebgl, this._webglScene);
        }
        if (this.settings.smartContent) {
            this._smartContentHandler = new SmartContentHandler(this.divWebgl, this._webglScene);
        }
        this._webglScene.render();
    }

    /**
    *
    * Reset scene to webgl, remove all items and recreate
    **/
    _refresh() : void {
        if (this.PlanManager === null) {
            return;
        }

        //Notify reload
        eventsManager.instance.dispatch(WebGLEvents.RELOAD);
        this._scene = this.PlanManager.getInstance().world.currentScene;

        if (this._webglScene) {
            this._webglScene.cleanUpDynamicHull();
            this._webglScene.updateScene(this._scene!);
        }
        this._getHulls();
    }

    displayCurrentFloorChanged(value: boolean) : void {
        if (this.PlanManager === null) {
            return;
        }

        if (!this._webglScene) {
            return;
        }

        this._webglScene.displayCurrentFloor(value);
    }

    _replaceWithObject(oldEntity: ArrangementObject, arrayObj: Array<any>, index: number, oldObjects: Array<Entity>, newObjectsAndParents: Array<{entity: Entity, parent: Entity}>, callback: CallableFunction) : void {
        createAssetEntity(_ASSET_TYPE.ARRANGEMENTS, arrayObj[index], true, (newEntity: any) => {
            if (oldEntity.parent && oldEntity.floor) {
                math.mat4.copy(newEntity.transform.localMatrix, oldEntity.transform.globalMatrix);
                // Save group altervatives
                newEntity.closeObjects = arrayObj;
                // Save current alternative number
                newEntity.closeCurrentObject = index;
                // Disable anchoring to prevent updateFloorHeight
                newEntity.isAnchorActive = false;

                // Adapt floor height according to object height change
                let position = oldEntity.position;
                if (oldEntity.anchor && oldEntity.anchor[1] === -3) {
                    position[2] += (oldEntity.realHeight - newEntity.realHeight) / 2;
                } else {
                    position[2] -= (oldEntity.realHeight - newEntity.realHeight) / 2;
                }
                newEntity.position = position;

                oldObjects.push(oldEntity);
                newObjectsAndParents.push({ entity: newEntity, parent: oldEntity.parent });
            }
            callback();
        });
    }

    _loadCloseObjects(entity: ArrangementObject, add: number, oldObjects: Array<Entity>, newObjectsAndParents: Array<{entity: Entity, parent: Entity}>, callback: CallableFunction) : void {
        let minlength;
        let maxlength;
        let percent = 10;

        if (entity.length > 400) {
            percent = 50;
        }

        minlength = entity.length - (entity.length * percent / 100);
        maxlength = entity.length + (entity.length * percent / 100);

        let url = getUrl() + 'api/v1/assets?q.objectType.eq=' + entity.objectType +
            '&fields=marketName,price,genericName,name,dimensions,sketchBlock,_id,marketLink,configs,stretchability,objectType,coatingType,manufacturer,retailer,pipeline,styles' +
            '&q.dimensions.width.bt=' + minlength + ',' + maxlength +
            '&q.pipeline.state.in=available,archived&sort=dimensions.width&q.pipeline.modelState.eq=validated';

        if (entity.objectStyles && entity.objectStyles.length > 0) {
            url += "&q.styles.in=";
            for (let i = 0; i < entity.objectStyles.length; i++) {
                url += entity.objectStyles[i]._id;
                if (i < entity.objectStyles.length - 1) {
                    url += ",";
                }
            }
        }
        url += '&apikey=' + getToken();

        HttpService.get(url)
            .then((response) => {
                //success
                if (response.status === 200 && response.data && response.data.resources && response.data.resources.length > 0) {
                    // We search for the current resource within the current returned data
                    let i = 0;
                    for (; i < response.data.resources.length; i++) {
                        if (entity.objectId === response.data.resources[i]._id) {
                            // Found
                            break;
                        }
                    }

                    // Not found search the closest
                    if (i >= response.data.resources.length) {
                        for (let i = 0; i < response.data.resources.length; i++) {
                            if (entity.length === response.data.resources[i].dimensions.width) {
                                // Found
                                break;
                            }

                            if (entity.length < response.data.resources[i].dimensions.width) {
                                // Found
                                if (add < 0) {
                                    i += add;
                                }
                                break;
                            }
                        }
                    } else {
                        i += add;
                    }

                    if (i >= response.data.resources.length) {
                        i = response.data.resources.length - 1;
                    }
                    if (i < 0) {
                        i = 0;
                    }

                    this._replaceWithObject(entity, response.data.resources, i, oldObjects, newObjectsAndParents, callback);
                }
                else {
                    this._displayWarning = true;
                    callback();
                }
            })
            .catch(() => {
                callback();
            });
    }

    _switchCloseObjects(entity: ArrangementObject, add: number, oldObjects: Array<Entity>, newObjectsAndParents: Array<{entity: Entity, parent: Entity}>, callback: CallableFunction) : void {
        if ((entity as any).closeObjects !== undefined) {
            if ((entity as any).closeObjects.length > 1) {
                let configNb = (entity as any).closeCurrentObject + add;
                if (configNb >= (entity as any).closeObjects.length) {
                    configNb >= (entity as any).closeObjects.length - 1;
                }
                if (configNb < 0) {
                    configNb = 0;
                }
                this._replaceWithObject(entity, (entity as any).closeObjects, configNb, oldObjects, newObjectsAndParents, callback);
            }
        }
    }

    _changeCamera(keyCode: number) : void {
        if (!this._webglScene) return;

        if (this._webglScene.camera && this._webglScene.camera.entity.locked === false) {
            const entity = this._webglScene.camera.entity;
            entity.startTemporary();
            if (entity.cameraType !== SceneConstants.CameraType.Perspective) {
                entity.fov = SceneConstants.CameraPerspectiveOriginalFOV;
            }
            switch (keyCode) {
                case 65: //a
                    entity.cameraType = SceneConstants.CameraType.Axonomic;
                    break;
                case 80: //p
                    entity.cameraType = SceneConstants.CameraType.Panoramic;
                    break;
                case 82: //r
                    entity.cameraType = SceneConstants.CameraType.Storage;
                    break;
                case 67: //h
                    if (entity.cameraType !== SceneConstants.CameraType.Perspective && entity.cameraType !== SceneConstants.CameraType.Storage) {
                        entity.cameraType = SceneConstants.CameraType.Perspective;
                    }
                    entity.format = SceneConstants.CameraPreset.NormalSquare;
                    break;
                case 72: //h
                    if (entity.cameraType !== SceneConstants.CameraType.Perspective && entity.cameraType !== SceneConstants.CameraType.Storage) {
                        entity.cameraType = SceneConstants.CameraType.Perspective;
                    }
                    entity.format = SceneConstants.CameraPreset.NormalPaysage;
                    break;
                case 86: //v
                    if (entity.cameraType !== SceneConstants.CameraType.Perspective && entity.cameraType !== SceneConstants.CameraType.Storage) {
                        entity.cameraType = SceneConstants.CameraType.Perspective;
                    }
                    entity.format = SceneConstants.CameraPreset.NormalPortrait;
                    break;
            }

            if (entity.cameraType !== (entity as any)._cameraType && this.PlanManager) {
                entity.updateCameraNb(this.PlanManager.getInstance().world);
            }

            let command = new Commands.EditRenderCameraCommand(entity, false);
            if (this.PlanManager) {
                this.PlanManager.getInstance().executeCommand(command);
            } else {
                command.execute();
            }
            this._webglScene.updateCamera(entity.id);
        }
    }

    _videoPreviewStep() : void {
        if (!this._webglScene) return;
        if (!this._webglScene.camera) return;
        if (!this.PlanManager) return;
        const allArrangements = this.PlanManager.getInstance().world.currentScene.arrangementObjects;
        const sortedArrangements = allArrangements.sort(function (a: Entity, b: Entity) { return a.id - b.id; });
        let i;
        const nbObjectsToUnhide = (sortedArrangements.length - this._videoPreviewNbUnhiddenObjects) / ((this._webglScene.camera.entity.nbImages - 1) - this._videoPreviewCurrentImage);

        for (i = this._videoPreviewNbUnhiddenObjects; i < this._videoPreviewNbUnhiddenObjects + nbObjectsToUnhide; i++) {
            const entity = this._webglScene.getPlanEntity(sortedArrangements[i].id);
            if (!entity) {
                continue;
            }

            this._webglScene.setLayer(entity.object!, 0);
        }
        this._webglScene.render();
        this._videoPreviewNbUnhiddenObjects = i;
        this._videoPreviewCurrentImage++;

        if (this._videoPreviewCurrentImage >= (this._webglScene.camera.entity.nbImages - 1)) {
            clearInterval(this._videoPreviewIntervalId);
            this._videoPreviewIntervalId = undefined;
        }
    }

    _setCurrentPhoto(photoNb: number) : void {
        if (!this._webglScene) return;
        if (this._webglScene.camera !== null) {
            this._photoFromCurrentProjectNb = photoNb;
            setTimeout(() => {
                this.displayPhoto = true;
                this._delegate.render();
            });
        }
    };

    _getOriginalPhotoUrl() : string {
        if (this._photoFromCurrentProjectNb === -1 || !this._webglScene) {
            return ("");
        }

        if (this.imgProject !== undefined) {
            let photoId = this._getPhotoId(this._webglScene.camera!)

            // Photo id not found
            if (photoId === -1) {
                return ("");
            }

            for (let i = 0; i < this.imgProject.length; i++) {
                if (this.imgProject[i].id === photoId) {
                    return this.imgProject[i].original_path;
                }
            }

            if (this.imgProject.length > this._photoFromCurrentProjectNb) {
                return this.imgProject[this._photoFromCurrentProjectNb].original_path;
            } else {
                return ("");
            }
        } else {
            return ("");
        }
    };

    _getPhotoUrl() : string {
        if (this._photoFromCurrentProjectNb === -1 || !this._webglScene) {
            return ("");
        }

        if (this.imgProject !== undefined) {
            let photoId = this._getPhotoId(this._webglScene.camera!)

            // Photo id not found
            if (photoId === -1) {
                return ("");
            }

            for (let i = 0; i < this.imgProject.length; i++) {
                if (this.imgProject[i].id === photoId) {
                    return this.imgProject[i].before_post_prod_path + "?" + performance.now();
                }
            }

            if (this.imgProject.length > this._photoFromCurrentProjectNb) {
                return this.imgProject[this._photoFromCurrentProjectNb].before_post_prod_path + "?" + performance.now();
            } else {
                return ("");
            }
        } else {
            return ("");
        }
    };

    _getPhotoId(camera: WebglCamera) : number {
        if (!camera) return -1;
        const photoName = camera.entity.name;
        // If no photo name then it was a fake draw photo self created then return invalid id -1
        if (photoName) {
            const photoNameParse = photoName.split('_');

            return Number(photoNameParse[photoNameParse.length - 1]);
        } else {
            return (-1);
        }
    };

    //INITILIAZE
    initialize() : void {
        if (this.jsonScene) {
            this._scene = JSONImporter.importScene(this.jsonScene);
        } else if (this.PlanManager) {
            // the variable is defined
            this._scene = this.PlanManager.getInstance().world.currentScene;
        }
        if (this.activeCameraId) {
            this._cameraState = CAMERA_STATES.COCOS;
        }
        this._init();
        this._getHulls();

        if (this._webglScene) {
            if (this._scene) {
                this._webglScene.updateScene(this._scene);
            }
            if (this.jsonScene ) {
                this._webglScene.updateCamera(this.activeCameraId);
            }
        }
    }

    destroy() : void {
        if (!this.bindedElement) return;
        console.log("Destroy WebGL");
        // Destroy
        if (this._webglScene !== null) {
            this._webglScene.destroy();
            this._webglScene = null;
        }
        //destroy
        this.bindedElement.removeEventListener("mousedown", this.mousedownHandler);
        this.bindedElement.removeEventListener("dblclick", this.dblclickHandler);
        this.bindedElement.removeEventListener("mouseup", this.mouseupHandler);
        this.bindedElement.removeEventListener("mousemove", this.mousemoveHandler);
        this.bindedElement.removeEventListener("mouseleave", this.mouseleaveHandler);
        this.bindedElement.removeEventListener("mouseover", this.mouseoverHandler);
        this.bindedElement.removeEventListener("wheel", this.wheelHandler);
        document.removeEventListener("keydown", this.keydownHandler);
        document.removeEventListener("keyup", this.keyupHandler);
        window.removeEventListener('resize', this._resize);
        //Destroy listeners
        eventsManager.instance.removeListener(this.coatingRotationListener);
        eventsManager.instance.removeListener(this.coatingDragListener);
        eventsManager.instance.removeListener(this.entityDragListener);
        eventsManager.instance.removeListener(this.setFilters);
        eventsManager.instance.removeListener(this.assetsUpdated);
        eventsManager.instance.removeListener(this.stopEnvUpdate);
        eventsManager.instance.removeListener(this.updateEnvs);
        eventsManager.instance.removeListener(this.computeCameraShoppingList);
        eventsManager.instance.removeListener(this.planStateChangedListener);
        eventsManager.instance.removeListener(this.projectLoaded);
        eventsManager.instance.removeListener(this.projectUpdated);
        eventsManager.instance.removeListener(this.refreshListener);
        eventsManager.instance.removeListener(this.hullLoadedListener);
        if (this.actionCountChange) {
            eventsManager.instance.removeListener(this.actionCountChange);
        }
        eventsManager.instance.removeListener(this.floorListener);
        eventsManager.instance.removeListener(this.reloadScene);
        eventsManager.instance.removeListener(this.fieldEditChange);
        eventsManager.instance.removeListener(this.resizeListener);
        eventsManager.instance.removeListener(this.sceneUpdated);
        eventsManager.instance.removeListener(this.fullscreenListener);
        eventsManager.instance.removeListener(this.displaySizeChanged);
        eventsManager.instance.removeListener(this.updateArrangementCoating);
        eventsManager.instance.removeListener(this.floorGeneratorChanged);
        eventsManager.instance.removeListener(this.floorGeneratorUpdated);
        eventsManager.instance.removeListener(this.frontViewFloorGeneratorChanged);
        eventsManager.instance.removeListener(this.hideAxo);
        eventsManager.instance.removeListener(this.hideArrangements);
        eventsManager.instance.removeListener(this.textFieldListener);
        eventsManager.instance.removeListener(this.textFieldEndListener);
        eventsManager.instance.removeListener(this.needPopulateListener);
    }

    _addTarget(parent: Entity, index: number) : void {
        getAsset(_ASSET_TYPE.ARRANGEMENTS, "5f49124f6c44d36fc0f1fb56", null, (result: any) => {
            createAssetEntity(_ASSET_TYPE.ARRANGEMENTS, result, false, (entity: Entity) => {
                if (!this._webglScene) return;
                entity.position = math.vec3.fromValues(0, 0, -parent.position[2]);
                parent.addChild(entity);
                this._webglScene.addEntity(entity, undefined, undefined, () => {
                    if (!this._webglScene) return;
                    let webglEntity = this._webglScene.getPlanEntity(entity.id)!;
                    let traversing = (child: THREE.Object3D) => {
                        child.receiveShadow = false;
                        (child as any).keepMaterial = true;
                    }
                    webglEntity.object!.traverse(traversing);
                    this._webglScene.setLayer(webglEntity.object!, index);
                });
                this._webglScene.render();
            });
        });
    }

    _loadEntity(id: string, config: any, lod: any, index?: number) : void {
        if (index !== undefined) {
            this._loadedEntity = [];
        } else {
            this._loadedEntity = null;
            index = 0;
        }
        getAsset(_ASSET_TYPE.ARRANGEMENTS, id, null, (result: any) => {
            let configIndex = config;
            if (config.startsWith && config.startsWith('lod')) {
                configIndex = config.split('_')[1];
            }
            configIndex = parseInt(configIndex);
            let height = result.configs[configIndex - 1].dimensions.height;
            createAssetEntity(_ASSET_TYPE.ARRANGEMENTS, result, false, (entity: ArrangementObject) => {
                if (!this._webglScene) return;
                let position = math.vec3.fromValues(0, Math.max(Math.max(entity.height, entity.width), entity.length) * 10 * index, height / 2);
                entity.position = position;
                if (Array.isArray(this._loadedEntity)) {
                    this._loadedEntity.push(entity);
                } else {
                    this._loadedEntity = entity;
                }
                this._addTarget(entity, index);
                this._webglScene.settings.meshLevel = this.meshLevel;
                this._webglScene.meshLevel = this.meshLevel;
                this._webglScene.forceProbes = true;
                this._scene!.addEntity(entity);
                this._webglScene.addEntity(entity, config, lod, () => {
                    if (!this._webglScene) return;
                    let webglEntity = this._webglScene.getPlanEntity(entity.id);
                    if (webglEntity && webglEntity instanceof WebglFurniture) {
                        this._webglScene.setLayer(webglEntity.object, index);
                        eventsManager.instance.dispatch(Events.AM_ENTITY_STATS, { index: index, statistics: webglEntity.statistics });
                    }
                });
                this._webglScene.updateSettings();
                this._webglScene.fitCamera(entity, index);
                this._webglScene.render();
            });
        });
    }

    _resizeCameras() : void {
        if (!this._webglScene) {
            return;
        }

        if (!this._webglScene.cameraArray) {
            return;
        }

        const cameras = this._webglScene.cameraArray;
        const lines = Math.floor(cameras.length / 3);
        const columns = 3;
        const fullWidth = this.divWebgl.parentElement!.offsetWidth;
        const fullHeight = this.divWebgl.offsetHeight;
        const width =  fullWidth / columns;
        const height = fullHeight / lines;
        for (let i = 0; i < cameras.length; ++i) {
            const camera = cameras[i];
            const line = Math.floor(i / 3);
            const column = Math.floor(i % 3);
            camera.setViewOffset(fullWidth, fullHeight, Math.floor(column * width), Math.floor(line * height), Math.ceil(width), Math.ceil(height));
            (camera as any).target = new THREE.Vector3();
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        }
    }

    display(value: number) : void {
        if (!this._webglScene) {
            return;
        }

        switch (value) {
            case 0:
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        (this._webglScene.getPlanEntity(this._loadedEntity[i].id) as WebglFurniture).updateCoating();
                    }
                } else {
                    (this._webglScene.getPlanEntity(this._loadedEntity!.id) as WebglFurniture).updateCoating();
                }
                break;
            case 1:
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        (this._webglScene.getPlanEntity(this._loadedEntity[i].id) as WebglFurniture).wireframe();
                    }
                } else {
                    (this._webglScene.getPlanEntity(this._loadedEntity!.id) as WebglFurniture).wireframe();
                }
                break;
            case 2:
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        (this._webglScene.getPlanEntity(this._loadedEntity[i].id) as WebglFurniture).normal();
                    }
                } else {
                    (this._webglScene.getPlanEntity(this._loadedEntity!.id) as WebglFurniture).normal();
                }
                break;
            case 3:
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        (this._webglScene.getPlanEntity(this._loadedEntity[i].id) as WebglFurniture).uvs('numbers');
                    }
                } else {
                    (this._webglScene.getPlanEntity(this._loadedEntity!.id) as WebglFurniture).uvs('numbers');
                }
                break;
            case 4:
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        (this._webglScene.getPlanEntity(this._loadedEntity[i].id) as WebglFurniture).uvs('checker');
                    }
                } else {
                    (this._webglScene.getPlanEntity(this._loadedEntity!.id) as WebglFurniture).uvs('checker');
                }
                break;
            case 5:
                if (Array.isArray(this._loadedEntity)) {
                    for (let i = 0; i < this._loadedEntity.length; ++i) {
                        (this._webglScene.getPlanEntity(this._loadedEntity[i].id) as WebglFurniture).uvs('lines');
                    }
                } else {
                    (this._webglScene.getPlanEntity(this._loadedEntity!.id) as WebglFurniture).uvs('lines');
                }
                break;
        }
        this._webglScene.render();
    }

    _resize = (updateCamera?: boolean | Event) : any => {
        setTimeout(() => { 
            if (updateCamera === undefined) updateCamera = true;
            if (this._webglScene) {
                if (this._smartContentHandler) {
                    this._smartContentHandler.Resize();
                    this._smartContentHandler.UpdateCamera();
                    this._webglScene.render();
                }
                else {
                    if (this._interactiveProjectHandler) {
                        this._interactiveProjectHandler.Resize();
                        this._interactiveProjectHandler.UpdateCamera();
                    }
                    else {
                        this._resizeCameras();
                        this._webglScene.leftPanel = this.leftPanel;
                        this._webglScene.updateEnvs();
                        if (!this._webglScene.camera) {
                            this._cameraState = CAMERA_STATES.FREE;
                        }
                        if (this._cameraState === CAMERA_STATES.COCOS && updateCamera && this._webglScene.camera) {
                            this._webglScene.updateCamera(this._webglScene.camera.entity.id);
                        } else if (this._cameraState === CAMERA_STATES.FREE) {
                            this._webglScene.updateCamera(null);
                        }
                        if (this._webglScene.camera) {
                            this.cameraHeight = (this._webglScene.camera.entity.transform.localPosition[2] / 10).toFixed(1);
                            this.cameraFocal = this._webglScene.camera.entity.fov.toFixed(1);
                            this._delegate.render();
                        }
                        eventsManager.instance.dispatch(Events.CAMERA_CHANGED);
                    }
                }
            }
        });
    }

    _resetZoom() : void {
        if (!this._webglScene) return;
        this.zoom = 1;
        this.transformOrigin = "center";
        this._transformOriginMouseDown = [0, 0];
        this._transformOriginOffset = [0, 0];
        this._webglScene.renderer.domElement.setAttribute("style", "transform: scale(" + this.zoom + "); transform-origin: " + this.transformOrigin);
        this._delegate.render();
    }

    constructor(delegate: { render: CallableFunction }, PlanManager: PlanManagerLike | null = null) {
        this.PlanManager = PlanManager
        this.displayPhoto = false;
        this._photoOpacity = 0.5;
        this._photoFromCurrentProjectNb = -1;
        this._transformOrigin = "center";
        this._transformOriginMouseDown = [0, 0];
        this._transformOriginOffset = [0, 0];

        this._videoPreviewIntervalId = undefined;
        this._videoPreviewNbUnhiddenObjects = 0;
        this._videoPreviewCurrentImage = 0;

        this._enable_additionals_afters = true;

        this._bPressed = false;
        this._vPressed = false;

        this._delegate = delegate;

        this.thirdGrid = false;
        this.loaded = false;
        this.zoom = 1;

        this._cameraState = CAMERA_STATES.FREE;
    }

    run(bindedElement: HTMLElement, divWebgl: HTMLElement, callback: CallableFunction) : void {

        this.bindedElement = bindedElement;
        this.divWebgl = divWebgl;
        PhysicsLoader(PLAN_WEBGL_MODULE_PATH + '/enable3d/ammo', () => {

            // Check if owner needs additional cameras or not (to hide the add camera button)
            if (this.user && this.user.platform.enable_additionals_afters !== undefined && this.user.platform.enable_additionals_afters === 0) {
                this._enable_additionals_afters = false;
            }

            this.settings = this.settings || JSON.parse(SavaneCookie.getCookie("Rhinov-PlanWebGL-Settings", '{\
                        "meshLevel": 1,\
                        "interactiveProject": false,\
                        "smartContent": false,\
                        "mirrors": true,\
                        "fxaa": true,\
                        "cellShading": true,\
                        "display": 0,\
                        "hullbin": true\
                    }'));

            if (this.meshLevel === undefined) {
                this.meshLevel = 1;
            }

            if (!this.settings.interactiveProject && !this.settings.smartContent) {
                this.loaded = true;
            }

            this.settings.display = 0;
            this._dragState = null;

            this._interactiveProjectHandler = null;

            this._mouseDownEvent = null;
            this._mouseMoveEvent = null;
            this._mouseOver = false;
            this._loadedEntity = null;

            this.updateFloorGeneratorTimeout = null;
            this.updateDynamicHullTimeout = null;
            this.editRenderCameraCommandTimeout = null;

            this.currentKeyPressed = null;
            this._loadedHullCount = 0;

            this.projectLoaded = eventsManager.instance.addListener(Events.PROJECT_DID_LOAD, this._getHulls.bind(this))!;
            this.projectUpdated = eventsManager.instance.addListener(Events.UPDATE_RHINOV_FORMAT, this._getHulls.bind(this))!;
            this.refreshListener = eventsManager.instance.addListener(Events.PROJECT_REFRESH_HULL, this._getDynamicHull.bind(this))!;

            this.hullLoadedListener = eventsManager.instance.addListener(Events.HULL_LOADED, () => {
                if (this._webglScene !== null) {
                    this.loaded = this._loadedHullCount >= 3;
                    this._webglScene.render();
                }
            })!;

            // Listener for COCOS Camera movement
            let cocosPosition = new THREE.Vector3();
            this.cocosCameraDragListener = eventsManager.instance.addListener(Events.POS_WIN_CHANGED, (event: SavaneEvent) => {
                if (this._webglScene !== null) {
                    cocosPosition.set(event.userData.x / 10, event.userData.y / 10, 150 / event.userData.zoom);
                    if (!this._webglScene.defaultCameraMoved) {
                        cocosPosition.z = this._webglScene.getDefaultCameraDistanceFit();
                        this._webglScene.defaultCamera.position.copy(cocosPosition);
                        (this._webglScene.defaultCamera as any).target = new THREE.Vector3(cocosPosition.x, cocosPosition.y, 0);
                        this._webglScene.render();
                    }
                }
            })!;

            // Listen to floor change and display current floor if asked
            this.floorListener = eventsManager.instance.addListener(Events.PLAN_FLOOR_CHANGED, (_event: any) => {
                if (this._webglScene) {
                    this._webglScene.detachSelection();
                    this._webglScene.visibleRooms = [];

                    this.displayCurrentFloorChanged(this.displayCurrentFloor);
                    eventsManager.instance.dispatch(Events.HIDE_ARRANGEMENTS);

                    if (this._webglScene.camera) {
                        if (this._webglScene.camera.entity.cameraType !== SceneConstants.CameraType.PhotoRender) {
                            this._cameraState = CAMERA_STATES.FREE;
                            this._webglScene.updateCamera(null);
                            eventsManager.instance.dispatch(Events.CAMERA_RESIZED);
                        } else {
                            this._webglScene.updateCamera(this._webglScene.camera.entity.id);
                        }
                    }
                }
            })!;

            // Listen to floor change and reload the entire webgl scene
            this.coatingRotationListener = eventsManager.instance.addListener(Events.COATING_ROTATION_CHANGED, (event: any) => {
                if (this._webglScene) {
                    this._webglScene.updateCoatingParameters(event.userData);
                }
            })!;

            this.coatingDragListener = eventsManager.instance.addListener(Events.COATING_DRAG, (data: any) => {
                this.bindedElement.style.cursor = 'grabbing';
                this._dragState = {
                    state: DRAG_STATE.COATING,
                    data: data.userData,
                    value: data.userData.coating
                };
            })!;

            this.entityDragListener = eventsManager.instance.addListener(Events.ENTITY_EDITION, (data: any) => {
                this.bindedElement.style.cursor = 'grabbing';
                this._dragState = {
                    state: DRAG_STATE.ENTITY,
                    data: data.userData,
                    value: data.userData.entity
                };
            })!;

            this.planStateChangedListener = eventsManager.instance.addListener(Events.PLAN_STATE_CHANGED, (state: any) => {
                if (state.userData !== 36 && state.userData !== 31 && state.userData !== 41 && state.userData !== 42 && !this._mouseOver) {
                    this.bindedElement.style.cursor = 'default';
                    this._dragState = null;
                }
            })!;

            this.setFilters = eventsManager.instance.addListener(Events.SET_FILTERS, (_event: any) => {
                //resize();
            })!;

            this.assetsUpdated = eventsManager.instance.addListener('results_update_datas', (_event: any) => {
                if (this._webglScene !== null) {
                    this._webglScene.updateEnvs();
                }
            })!;

            this.stopEnvUpdate = eventsManager.instance.addListener(Events.STOP_UPDATING_ENVS, (_event: any) => {
                if (this._webglScene !== null) {
                    this._webglScene.stopEnvUpdate();
                }
            })!;

            this.updateEnvs = eventsManager.instance.addListener(Events.START_UPDATING_ENVS, (_event: any) => {
                if (this._webglScene !== null) {
                    this._webglScene.updateEnvs();
                }
            })!;

            this.computeCameraShoppingList = eventsManager.instance.addListener(Events.COMPUTE_CAMERA_SHOPPING_LIST, (event: any) => {
                if (this._webglScene !== null) {
                    this._webglScene.computeCameraShoppingList(event.userData, this.user);
                }
            })!;

            if (!this.settings.interactiveProject && !this.settings.smartContent) {
                // Listener for command proceed in COCOS that will cause an update into the webGL view (move an arrangement objet, group arrangement objects or groups)
                this.actionCountChange = eventsManager.instance.addListener(Events.COMMAND_EXECUTED, async (event: any) => {
                    // If the webGL scene exists
                    if (this._webglScene !== null) {
                        // Depending on the command name (see Command.js from CocosPlanModule/src/cocos/scripts/command)
                        switch (event.userData.name) {

                            case Commands.CommandEnum.EditRenderCameraCommand:
                                this._webglScene.showExcludedObject();
                                this._webglScene.hideExcludedObject(this._webglScene.getPlanCamera(event.userData.datas.id)!);
                                this._webglScene.updateCamera(event.userData.datas.id);
                                this._resize(false);
                                break;

                            // Edit entity command : edition of cameras, lights, arrangement object and group
                            case Commands.CommandEnum.EditGeometryPrimitiveCommand:
                            case Commands.CommandEnum.EditTechnicalElementCommand:
                            case Commands.CommandEnum.EditJoineryCommand:
                            case Commands.CommandEnum.EditStaircaseCommand:
                            case Commands.CommandEnum.EditRoomPropertiesCommand:
                            case Commands.CommandEnum.EditEntityCommand:
                                // We do this code for both execute and undo

                                // If we edit a camera
                                if (event.userData.datas.type === SceneConstants.EntityType.RenderCamera) {
                                    // The camera is now used for the preview
                                    this._cameraState = CAMERA_STATES.COCOS;
                                    if (event.userData.datas.select) {
                                        this._webglScene.updateCamera(event.userData.datas.id);
                                        if (this._webglScene.camera) {
                                            if (this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.PhotoRender) {
                                                if (this._webglScene.sun.entity.exterior === 'defaut') {
                                                    this._photoFromCurrentProjectNb = this._webglScene.camera.entity.cameraNb;
                                                    const texture = new THREE.TextureLoader().load(this._getPhotoUrl());
                                                    texture.colorSpace = THREE.SRGBColorSpace;
                                                    this._webglScene.threeScene.background = texture;
                                                    this._webglScene.toggleSky(false);
                                                } else {
                                                    this._webglScene.loadBackground(this._webglScene.sun.entity, false);
                                                }
                                            } else if (this._webglScene.camera && this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Axonomic) {
                                                this._webglScene.threeScene.background = null;
                                                this._webglScene.toggleSky(true);
                                            } else {
                                                let additional = (this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Perspective || this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Storage) && this.user && this.user.platforms_id !== 1;
                                                this._webglScene.loadBackground(this._webglScene.sun.entity, additional);
                                            }
                                        }
                                    } else {
                                        if (this._webglScene.camera) {
                                            this._webglScene.camera.targetInitialized = false;
                                        }
                                        this._webglScene.updateCamera(event.userData.datas.id);
                                    }
                                    this._resize(false);
                                }
                                else {
                                    // Get webGL entity
                                    let webglEntity = this._webglScene.getPlanEntity(event.userData.datas.id);
                                    // If it exists
                                    if (webglEntity !== null) {
                                        //  We will go up the hierarchy
                                        let rootArrangementEntity = webglEntity.entity;

                                        // Until we find the root arrangement
                                        if (rootArrangementEntity.parent) {
                                            while (rootArrangementEntity.parent &&
                                                (rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementGroup || rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementObject ||
                                                 rootArrangementEntity.parent.entityType === SceneConstants.EntityType.GeometryGroup)) {
                                                rootArrangementEntity = rootArrangementEntity.parent;
                                            }
                                        }

                                        // And we update the whole tree underneath so all positions are updated
                                        this._webglScene.updateTree(rootArrangementEntity);
                                    }
                                }
                                break;

                            // Entity addition to the scene, we need to create the new entity into the webGL scene
                            case Commands.CommandEnum.AddEntityCommand:
                                // Undo or execute ?
                                if (event.userData.undo) {
                                    // Undo, we remove the entity tree from the webGL scene (undo addition = destruction)
                                    this._webglScene.removeEntityTree(event.userData.datas.entity);
                                }
                                else {
                                    // Execute, we add the entity tree to the webGL scene
                                    this._webglScene.addEntityTree(event.userData.datas.entity);

                                    // Get webGL entity
                                    let webglEntity = this._webglScene.getPlanEntity(event.userData.datas.id);
                                    // If it exists
                                    if (webglEntity !== null) {
                                        //  We will go up the hierarchy
                                        let rootArrangementEntity = webglEntity.entity;

                                        // Until we find the root arrangement
                                        if (rootArrangementEntity.parent) {
                                            while (rootArrangementEntity.parent && (rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementGroup || rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementObject)) {
                                                rootArrangementEntity = rootArrangementEntity.parent;
                                            }
                                        }

                                        // And we update the whole tree underneath so all positions are updated
                                        this._webglScene.updateTree(rootArrangementEntity);
                                    }
                                }
                                break;

                            case Commands.CommandEnum.AddEntitiesCommand:
                                // Undo or execute ?
                                if (event.userData.undo) {
                                }
                                else {
                                    for (let i = 0; i < event.userData.datas.entitiesAndParents.length; i++) {
                                        // And we update the whole tree underneath so all positions are updated
                                        this._webglScene.updateTree(event.userData.datas.entitiesAndParents[i].entity);
                                    }
                                }
                                break;

                            // Entity deletion from the scene
                            case Commands.CommandEnum.DeleteEntityCommand:
                            case Commands.CommandEnum.DeleteTechnicalElementCommand:
                            case Commands.CommandEnum.DeleteStaircaseCommand:
                                // Undo or execute ?
                                if (event.userData.undo) {
                                    // Undo, we add the entity tree to the webGL scene (undo deletion = addition)
                                    this._webglScene.addEntityTree(event.userData.datas.entity);

                                    // Get webGL entity
                                    let webglEntity = this._webglScene.getPlanEntity(event.userData.datas.id);
                                    // If it exists
                                    if (webglEntity !== null) {
                                        //  We will go up the hierarchy
                                        let rootArrangementEntity = webglEntity.entity;

                                        // Until we find the root arrangement
                                        if (rootArrangementEntity.parent) {
                                            while (rootArrangementEntity.parent && (rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementGroup || rootArrangementEntity.parent.entityType === SceneConstants.EntityType.ArrangementObject)) {
                                                rootArrangementEntity = rootArrangementEntity.parent;
                                            }
                                        }

                                        // And we update the whole tree underneath so all positions are updated
                                        this._webglScene.updateTree(rootArrangementEntity);
                                    }
                                }
                                else {
                                    // Execute, we delete the entity tree from the webGL scene
                                    this._webglScene.removeEntityTree(event.userData.datas.entity);
                                }
                                break;

                            // Arrangement group creation
                            case Commands.CommandEnum.CreateGroupCommand:
                                this._webglScene.detachSelection();
                                // Undo or execute ?
                                if (event.userData.undo) {
                                    // Undo, we remove all grouped objects from the webGL scene and add them again because their parent has changed (we need to remove them so they are removed from
                                    // their original webGL parent and we add them back to attach them to their new parent)
                                    // Warning, we don't reove the group entity tree because it has been emptied before the execution arrives here so we use the arrangement field of the execDatas parameter
                                    for (let i = 0; i < event.userData.datas.arrangements.length; i++) {
                                        this._webglScene.removeEntityTree(event.userData.datas.arrangements[i]);
                                        this._webglScene.addEntityTree(event.userData.datas.arrangements[i]);
                                    }
                                    // Remove the created group from the scene
                                    this._webglScene.removeEntity(event.userData.datas.group);
                                }
                                else {
                                    // Execute, we remove all grouped objects from the webGL scene and we will add them back along with the created group at the end (this will be done recursively by
                                    // the addEntityTree function)
                                    for (let i = 0; i < event.userData.datas.arrangements.length; i++) {
                                        this._webglScene.removeEntityTree(event.userData.datas.arrangements[i]);
                                    }
                                    this._webglScene.addEntityTree(event.userData.datas.group);
                                }
                                this._webglScene.attachSelection();
                                break;

                            // Arrangement group deletion
                            case Commands.CommandEnum.DeleteGroupCommand:
                                this._webglScene.detachSelection();
                                // Undo or execute ?
                                if (event.userData.undo) {
                                    // Undo, we add the group back to the scene, we will remove the children trees from the webGL scene
                                    for (let i = 0; i < event.userData.datas.group.iArrangements.length; i++) {
                                        this._webglScene.removeEntityTree(event.userData.datas.group.iArrangements[i]);
                                    }
                                    // And we add the group entity tree to the webGL scene (this is done recursively by the addEntityTree function)
                                    this._webglScene.addEntityTree(event.userData.datas.group);
                                }
                                else {
                                    // Execute, we remove the groupe entity tree from the scene (done recursively)
                                    this._webglScene.removeEntityTree(event.userData.datas.group);
                                    // And we add back all children to the webGL scene
                                    for (let i = 0; i < event.userData.datas.group.iArrangements.length; i++) {
                                        this._webglScene.addEntityTree(event.userData.datas.group.iArrangements[i]);
                                    }
                                }
                                this._webglScene.attachSelection();
                                break;

                            // Entities leaving a group
                            case Commands.CommandEnum.LeaveGroupCommand:
                                this._webglScene.detachSelection();
                                // We simply remove and add the entity leaving the group for both execute and undo
                                this._webglScene.removeEntityTree(event.userData.datas.entity);
                                this._webglScene.addEntityTree(event.userData.datas.entity);
                                this._webglScene.attachSelection();
                                break;

                            // Merge or multiple arrangement groups
                            case Commands.CommandEnum.MergeGroupCommand:
                                this._webglScene.detachSelection();
                                // Undo or execute ?
                                if (event.userData.undo) {
                                    // Undo the groups are unmerged, remove the merged group
                                    this._webglScene.removeEntityTree(event.userData.datas.group);

                                    // And re-create original groups
                                    for (let i = 0; i < event.userData.datas.arrangements.length; i++) {
                                        this._webglScene.removeEntityTree(event.userData.datas.arrangements[i]);
                                        this._webglScene.addEntityTree(event.userData.datas.arrangements[i]);
                                    }
                                }
                                else {
                                    // Execute, remove each merged entity tree from the scene
                                    for (let i = 0; i < event.userData.datas.arrangements.length; i++) {
                                        this._webglScene.removeEntityTree(event.userData.datas.arrangements[i]);
                                    }

                                    // And add the created group tree (already containing the entities above as children) to the webGL scene
                                    this._webglScene.addEntityTree(event.userData.datas.group);
                                }
                                this._webglScene.attachSelection();
                                break;

                            // Geometry group creation
                            case Commands.CommandEnum.CreateGeometryGroupCommand:
                                this._webglScene.detachSelection();
                                if (event.userData.undo) {
                                    // Undo the groups are unmerged, remove the merged group
                                    this._webglScene.removeEntityTree(event.userData.datas.group);
                                    event.userData.datas.geometries.forEach((element: Entity) => {
                                        this._webglScene!.removeEntityTree(element);
                                    });
                                    event.userData.datas.geometries.forEach((element: Entity) => {
                                        this._webglScene!.addEntityTree(element);
                                    });
                                }
                                else {
                                    // And add the created group tree (already containing the entities above as children) to the webGL scene
                                    event.userData.datas.geometries.forEach((element: Entity) => {
                                        this._webglScene!.removeEntityTree(element);
                                    });
                                    this._webglScene.addEntityTree(event.userData.datas.group);
                                }
                                await this._getDynamicHull();
                                this._webglScene.attachSelection();
                                break;

                            case Commands.CommandEnum.DeleteGeometryGroupCommand:
                                this._webglScene.detachSelection();
                                // Undo or execute ?
                                if (event.userData.undo) {
                                    event.userData.datas.geometries.forEach((element: Entity) => {
                                        this._webglScene!.removeEntityTree(element);
                                    });
                                    // And we add the group entity tree to the webGL scene (this is done recursively by the addEntityTree function)
                                    this._webglScene.addEntityTree(event.userData.datas.group);
                                }
                                else {
                                    // Execute, we remove the groupe entity tree from the scene (done recursively)
                                    this._webglScene.removeEntityTree(event.userData.datas.group);
                                    event.userData.datas.geometries.forEach((element: Entity) => {
                                        this._webglScene!.removeEntityTree(element);
                                    });
                                    event.userData.datas.geometries.forEach((element: Entity) => {
                                        this._webglScene!.addEntityTree(element);
                                    });
                                }
                                await this._getDynamicHull();

                                let coatings = event.userData.datas.group.getComponents(ComponentConstants.ComponentType.Coating) as Array<Coating>;
                                coatings = coatings.filter(item => item.hangType === Coating.HangType.technicalElement);
                                if (coatings.length > 0 && coatings[0].floorGeneratorSettings) {
                                    await this._getFloorGeneratorHull();
                                }

                                this._webglScene.attachSelection();
                                break;

                            case Commands.CommandEnum.AddComponentCommand:
                            case Commands.CommandEnum.ChangeCoatingCommand:
                            case Commands.CommandEnum.EditComponentCommand:
                                this._webglScene.detachSelection();
                                if (event.userData.datas.component.componentType === ComponentConstants.ComponentType.CoatingArea || event.userData.datas.component.componentType === ComponentConstants.ComponentType.Area) {
                                    eventsManager.instance.dispatch(Events.FRONTVIEW_FLOOR_GENERATOR_CHANGED);
                                } else if (event.userData.datas.component.componentType === ComponentConstants.ComponentType.FloorCoatingArea) {
                                    eventsManager.instance.dispatch(Events.FLOOR_GENERATOR_CHANGED);
                                } else {
                                    let entity = event.userData.datas.entity || event.userData.datas.component.entity;
                                    if (entity && entity.isArrangementObjectEntity()) {
                                        let glEntity = this._webglScene.getPlanEntity(entity.id);
                                        if (glEntity instanceof WebglFurniture) {
                                            glEntity.updateCoating();
                                        }
                                    } else {
                                        this._webglScene.updateHull(entity);
                                    }
                                }
                                this._webglScene.attachSelection();
                                break;

                            case Commands.CommandEnum.DeleteComponentCommand:
                                if (event.userData.datas.component.componentType === ComponentConstants.ComponentType.CoatingArea || event.userData.datas.component.componentType === ComponentConstants.ComponentType.Area) {
                                    this._getDynamicHull();
                                } else if (event.userData.datas.component.componentType === ComponentConstants.ComponentType.FloorCoatingArea) {
                                    this._getFloorGeneratorHull();
                                }
                                break;

                            case Commands.CommandEnum.ApplySmartDesignerSolutionCommand:
                                for (let i = 0; i < event.userData.datas.coatings.length; i++) {
                                    let entity = this._scene!.getDeepChild(event.userData.datas.coatings[i].forEntityId);

                                    if (entity) {
                                        this._webglScene.updateHull(entity);
                                    }
                                }
                                this._getDynamicHull();
                                this._getFloorGeneratorHull();
                                break;
                            case Commands.CommandEnum.ApplySmartRestylerSolutionCommand:
                                for (let i = 0; i < event.userData.datas.coatings.length; i++) {
                                    let entity = this._scene!.getDeepChild(event.userData.datas.coatings[i].forEntityId);

                                    if (entity) {
                                        this._webglScene.updateHull(entity);
                                    }
                                }
                                this._getDynamicHull();
                                this._getFloorGeneratorHull();
                                break;
                        }

                        this._webglScene.updateEnvs();
                        this._webglScene.render();
                    }
                    else {
                        this._refresh();
                    }
                })!;
            }

            this.fieldEditChange = eventsManager.instance.addListener(Events.ENTITY_PARAM_EDITED, (event: any) => {
                if (!this._webglScene) {
                    return;
                }

                if (event.userData.type === SceneConstants.EntityType.RenderCamera) {
                    this._cameraState = CAMERA_STATES.COCOS;
                    this._webglScene.updateCamera(event.userData.id);
                    this._resize(false);
                    if (event.userData.key === 'cameraType' || event.userData.key === undefined) {
                        if (this._webglScene.camera && this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Axonomic) {
                            this._webglScene.threeScene.background = null;
                            this._webglScene.toggleSky(true);
                        } else {
                            if (this._webglScene.sun.entity.exterior === 'defaut' && this._webglScene.camera) {
                                this._photoFromCurrentProjectNb = this._webglScene.camera.entity.cameraNb;
                                const texture = new THREE.TextureLoader().load(this._getPhotoUrl());
                                texture.colorSpace = THREE.SRGBColorSpace;
                                this._webglScene.threeScene.background = texture;
                                this._webglScene.toggleSky(false);
                            } else {
                                let additional = this._webglScene.camera && (this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Perspective || this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Storage) && this.user && this.user.platforms_id !== 1;
                                this._webglScene.loadBackground(this._webglScene.sun.entity, additional);
                            }
                        }
                    }
                } else if (event.userData.type === SceneConstants.EntityType.Sun) {
                    if ((event.userData.key === 'exterior' || event.userData.key === 'additionalExterior' || event.userData.key === 'decorsRotation') && this._webglScene.camera) {
                        if (this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.PhotoRender) {
                            if (this._webglScene.sun.entity.exterior === 'defaut') {
                                this._photoFromCurrentProjectNb = this._webglScene.camera.entity.cameraNb;
                                const texture = new THREE.TextureLoader().load(this._getPhotoUrl());
                                texture.colorSpace = THREE.SRGBColorSpace;
                                this._webglScene.threeScene.background = texture;
                                this._webglScene.toggleSky(false);
                            } else {
                                this._webglScene.loadBackground(this._webglScene.sun.entity, false);
                            }
                        } else {
                            let additional = (this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Perspective || this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Storage) && this.user && this.user.platforms_id !== 1;
                            this._webglScene.loadBackground(this._webglScene.sun.entity, additional);
                        }
                    }
                    this._webglScene.sun.update();
                } else {
                    let webglEntity = this._webglScene.getPlanEntity(event.userData.id);
                    if (webglEntity !== null) {
                        let attached = false;
                        if (this._webglScene.interactionWithItemAllowed(webglEntity.entity)) {
                            attached = this._webglScene.detachFromSelection(webglEntity as any);
                        }
                        webglEntity.update();
                        if (attached && this._webglScene.interactionWithItemAllowed(webglEntity.entity)) {
                            this._webglScene.attachToSelection(webglEntity as any);
                        }
                    }
                }
                this._webglScene.updateEnvs();
                this._webglScene.render();
            })!;

            this.updateArrangementCoating = eventsManager.instance.addListener(Events.UPDATE_ARRANGEMENT_COATING, (event: any) => {
                if (this._webglScene === null) {
                    return;
                }

                let webglEntity = this._webglScene.getPlanEntity(event.userData.id);
                if (!webglEntity) {
                    return;
                }

                if (webglEntity instanceof WebglFurniture) {
                    webglEntity.updateCoating();
                }
            })!;

            this.floorGeneratorChanged = eventsManager.instance.addListener(Events.FLOOR_GENERATOR_CHANGED, () => {
                if (this.updateFloorGeneratorTimeout) {
                    clearTimeout(this.updateFloorGeneratorTimeout);
                }
                this.updateFloorGeneratorTimeout = setTimeout(() => {
                    this.updateFloorGeneratorTimeout = null;
                    this._getFloorGeneratorHull();
                }, 1500);
            })!;

            this.cancelFloorGeneratorChanged = eventsManager.instance.addListener(Events.CANCEL_FLOOR_GENERATOR_CHANGED, () => {
                if (this.updateFloorGeneratorTimeout) {
                    clearTimeout(this.updateFloorGeneratorTimeout);
                    this.updateFloorGeneratorTimeout = null;
                }
            })!;

            this.frontViewFloorGeneratorChanged = eventsManager.instance.addListener(Events.FRONTVIEW_FLOOR_GENERATOR_CHANGED, () => {
                if (this.updateDynamicHullTimeout) {
                    clearTimeout(this.updateDynamicHullTimeout);
                }
                this.updateDynamicHullTimeout = setTimeout(() => {
                    this.updateDynamicHullTimeout = null;
                    this._getDynamicHull();
                }, 1500);
            })!;

            this.floorGeneratorUpdated = eventsManager.instance.addListener(Events.FLOOR_GENERATOR_UPDATED, (event: any) => {
                if (this._webglScene && this._webglScene.floorGeneratorHull) {
                    this._webglScene.floorGeneratorHull.UpdateComponent(event.userData.component, event.userData.offset);
                }
            })!;

            this.sceneUpdated = eventsManager.instance.addListener(Events.RHINOV_FORMAT_UPDATED, (event: any) => {
                this._scene = event.userData.scene;
            })!;

            this.resizeListener = eventsManager.instance.addListener(Events.CAMERA_RESIZED, () => {
                this._resize();
            })!;

            this.fullscreenListener = eventsManager.instance.addListener(Events.FULLSCREEN_CHANGED, () => {
                this._resize();
            })!;

            this.displaySizeChanged = eventsManager.instance.addListener('display_size_changed', () => {
                this._resize();
            })!;

            this.reloadScene = eventsManager.instance.addListener(Events.PROJECT_RELOAD, this._refresh.bind(this))!;

            this.hideAxo = eventsManager.instance.addListener(Events.HIDE_AXO, () => {
                if (!this._webglScene) {
                    return;
                }

                if (this.PlanManager === null) {
                    return;
                }

                if (this._webglScene.staticHull) {
                    if (this.PlanManager.getInstance()._hideAxo) {
                        this._webglScene.staticHull.hideCeiling();
                    } else {
                        this._webglScene.staticHull.showCeiling();
                    }
                }
                if (this._webglScene.dynamicHull) {
                    if (this.PlanManager.getInstance()._hideAxo) {
                        this._webglScene.dynamicHull.hideCeiling();
                    } else {
                        this._webglScene.dynamicHull.showCeiling();
                    }
                }
                if (this._webglScene.floorGeneratorHull) {
                    if (this.PlanManager.getInstance()._hideAxo) {
                        this._webglScene.floorGeneratorHull.hideCeiling();
                    } else {
                        this._webglScene.floorGeneratorHull.showCeiling();
                    }

                }
                this._webglScene.render();
            })!;

            this.hideArrangements = eventsManager.instance.addListener(Events.HIDE_ARRANGEMENTS, () => {
                if (!this._webglScene) {
                    return;
                }

                if (this.PlanManager === null) {
                    return;
                }

                if (this.PlanManager.getInstance()._hideArrangements) {
                    this._webglScene.hideArrangements();
                } else {
                    this._webglScene.showArrangements(this.displayCurrentFloor);
                }
                this._webglScene.render();
            })!;

            let textFieldCallback = (event: any) => {
                if (this._webglScene) {
                    const entity = this._webglScene.getPlanEntity(event.getUserData().obj.id);
                    if (entity) {
                        this._webglScene.detachSelection();
                        entity.update();
                        this._webglScene.attachSelection();
                        this._webglScene.render();
                    }
                }
            }

            this.textFieldListener = eventsManager.instance.addListener(Events.TEXTFIELD_EDIT, textFieldCallback)!;
            this.textFieldEndListener = eventsManager.instance.addListener(Events.TEXTFIELD_EDIT_ENDED, textFieldCallback)!;
            this.needPopulateListener = eventsManager.instance.addListener(Events.NEED_POPULATE, (event: any) => {
                this.SwitchPopulate(0, event.userData.callback);
            })!;

            this.mousedownHandler = (event: MouseEvent) => {
                event.stopPropagation();

                if (this._webglScene && this._webglScene.renderer) {
                    if (event.target !== this._webglScene.renderer.domElement && this.zoom === 1) {
                        return;
                    }
                }

                // Hide tulip will commit current changes
                if (this._webglScene && this._webglScene.gizmo.dragging === false) {
                    if (this.PlanManager && (this.PlanManager.getInstance().state as any).actionEditEnded !== undefined) {
                        (this.PlanManager.getInstance().state as any).actionEditEnded();
                        eventsManager.instance.dispatch(Events.HIDE_TULIP, null);
                    }
                }

                if (event.button === 1) {
                    event.preventDefault();
                }
                if (!this._webglScene) {
                    return;
                }

                if (this.settings.interactiveProject && this._interactiveProjectHandler) {
                    this._interactiveProjectHandler.HandleMouseDown(event);
                }

                if (this.settings.smartContent && this._smartContentHandler) {
                    this._smartContentHandler.HandleMouseDown(event);
                    return;
                }

                this._mouseDownEvent = {};
                for (let item in event) {
                    this._mouseDownEvent[item] = (event as any)[item];
                }

                if (!this.controls) {
                    return;
                }

                switch (this._mouseDownEvent.button) {
                    case 1:
                        if (this._mouseDownEvent.altKey && this._webglScene && this.zoom !== 1) {
                            this._transformOriginMouseDown = [event.clientX, event.clientY];
                            return;
                        }
                    case 2:
                        if (this._webglScene.camera && this._webglScene.camera.entity.locked === false) {
                            this._webglScene.camera.entity.startTemporary();
                        }
                        break;
                }

                this._webglScene.stopEnvUpdate();
                if (!this.settings.interactiveProject && !this.settings.smartContent) {
                    this._webglScene.selectionBoxHelper.isDown = false;
                    let selection = this._webglScene.selection();
                    selection = selection.filter((item: any) => {
                        return this._webglScene!.interactionWithItemAllowed(item);
                    });
                    if (event.ctrlKey && !event.altKey && selection.length === 0) {
                        this._webglScene.selectionBox.startPoint.set(
                            (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                            (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                            0.5
                        );
                        this._webglScene.selectionBoxHelper.isDown = true;
                        return;
                    } else {
                        this._webglScene.selectionBoxHelper.onSelectOver();
                    }
                }

                eventsManager.instance.dispatch(MOUSE_EVENTS.DOWN, {
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                    offsetX: event.offsetX,
                    offsetY: event.offsetY,
                    button: event.button,
                    ctrl: event.ctrlKey,
                    alt: event.altKey,
                    shift: event.shiftKey
                });

                this._webglScene.render();
            }

            this.bindedElement.addEventListener("mousedown", this.mousedownHandler);

            this.dblclickHandler = (event: MouseEvent) => {
                event.stopPropagation();
                if (this._webglScene && this._webglScene.renderer) {
                    if (event.target !== this._webglScene.renderer.domElement) {
                        return;
                    }
                }

                eventsManager.instance.dispatch(MOUSE_EVENTS.DOUBLE_CLICK, {
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                    offsetX: event.offsetX,
                    offsetY: event.offsetY,
                    button: event.button,
                    ctrl: event.ctrlKey,
                    alt: event.altKey,
                    shift: event.shiftKey
                });

                if (this._webglScene) {
                    this._webglScene.render();
                }
            }

            this.bindedElement.addEventListener("dblclick", this.dblclickHandler);

            this.mousemoveHandler = (event: MouseEvent) => {
                event.stopPropagation();

                if (!this._webglScene) {
                    return;
                }

                if (this._webglScene && this._webglScene.renderer) {
                    if (event.target !== this._webglScene.renderer.domElement && this.zoom === 1) {
                        return;
                    }
                }

                if (!this.loaded) {
                    return;
                }

                this._mouseMoveEvent = {};
                for (let item in event) {
                    this._mouseMoveEvent[item] = (event as any)[item];
                }

                if (this.settings.interactiveProject && this._interactiveProjectHandler) {
                    this._interactiveProjectHandler.Hovering(event);
                }

                if (!this._mouseDownEvent) {
                    return;
                }

                const dx = event.offsetX - this._mouseDownEvent.offsetX;
                const dy = event.offsetY - this._mouseDownEvent.offsetY;

                this._mouseDownEvent.offsetX = event.offsetX;
                this._mouseDownEvent.offsetY = event.offsetY;

                if (!this.controls) {
                    return;
                }

                this._webglScene.updateEnvs();
                switch (this._mouseDownEvent.button) {
                    case 1:
                        if (this._mouseDownEvent.altKey && this._webglScene && this.zoom !== 1) {
                            this._webglScene.selectionBoxHelper.isDown = false;
                            this._transformOriginOffset = [this._transformOriginOffset[0] + this.zoom * (this._transformOriginMouseDown[0] - event.clientX), this._transformOriginOffset[1] + this.zoom * (this._transformOriginMouseDown[1] - event.clientY)];
                            this._transformOriginMouseDown = [event.clientX, event.clientY];
                            this.transformOrigin = (this._transformOriginOffset[0] + this._mouseDownEvent.target.clientWidth / 2) + "px " + (this._transformOriginOffset[1] + this._mouseDownEvent.target.clientHeight / 2) + "px";
                            this._webglScene.renderer.domElement.setAttribute("style", "transform: scale(" + this.zoom + "); transform-origin: " + this.transformOrigin);
                            this._delegate.render();
                            return;
                        }
                        if (event.altKey) {
                            this._webglScene.rotateCamera(-dx / (event.target as HTMLElement).clientWidth, dy / (event.target as HTMLElement).clientHeight, 400);
                        }
                        else if (event.ctrlKey) {
                            this._webglScene.dollyCamera((dy / (event.target as HTMLElement).clientHeight) * 400, {
                                x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                                y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1
                            });
                        }
                        else {
                            this._webglScene.panCamera(this._mouseDownEvent.offsetX, this._mouseDownEvent.offsetY, dx, dy);
                        }
                        break;
                    case 2:
                        this._webglScene.focalCamera((dy / (event.target as HTMLElement).clientHeight) * 60);
                }

                if (this._webglScene.camera) {
                    this.cameraHeight = (this._webglScene.camera.entity.transform.localPosition[2] / 10).toFixed(1);
                    this.cameraFocal = this._webglScene.camera.entity.fov.toFixed(1);
                    this._delegate.render();
                }

                if (!this.settings.interactiveProject && !this.settings.smartContent) {
                    if (event.shiftKey && this._webglScene.selectionBoxHelper.isDown) {
                        this._webglScene.selectionBox.endPoint.set(
                            (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                            (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                            0.5
                        );
                    }
                }

                eventsManager.instance.dispatch(MOUSE_EVENTS.MOVE, {
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                    dx: dx / (event.target as HTMLElement).clientWidth,
                    dy: dy / (event.target as HTMLElement).clientHeight,
                    offsetX: event.offsetX,
                    offsetY: event.offsetY,
                    button: this._mouseDownEvent ? this._mouseDownEvent.button : null
                });

                this._webglScene.render();
            }

            this.bindedElement.addEventListener("mousemove", this.mousemoveHandler);

            this.mouseleaveHandler = (_event: MouseEvent) => {
                if (this._mouseOver) {
                    if (this._mouseDownEvent) {
                        switch (this._mouseDownEvent.button) {
                            case 1:
                            case 2:
                                if (this._webglScene && this._webglScene.camera && this._webglScene.camera.entity.locked === false) {
                                    let command = new Commands.EditRenderCameraCommand(this._webglScene.camera.entity, false);
                                    if (this.PlanManager) {
                                        this.PlanManager.getInstance().executeCommand(command);
                                    } else {
                                        command.execute();
                                    }   
                                }
                                break;
                        }
                    }

                    this.bindedElement.style.cursor = 'default';
                    this._mouseDownEvent = null;
                    this._mouseOver = false;
                    this.currentKeyPressed = null;
                    if (this._dragState) {
                        if (this._dragState.state === DRAG_STATE.COATING) {
                            eventsManager.instance.dispatch(Events.COATING_DRAG, this._dragState.data);
                        } else if (this._dragState.state === DRAG_STATE.ENTITY) {
                            eventsManager.instance.dispatch(Events.ENTITY_EDITION, this._dragState.data);
                        }
                    }
                    if (this._webglScene) {
                        this._webglScene.keyPressed = {};
                        if (this.settings.interactiveProject && this._interactiveProjectHandler) {
                            this._interactiveProjectHandler._clearHovering();
                        }
                        this._webglScene.render();
                    }
                }
            }

            this.bindedElement.addEventListener("mouseleave", this.mouseleaveHandler);

            this.mouseoverHandler = (_event: MouseEvent) => {
                if (this.noFocus) {
                    return;
                }

                if (!this._mouseOver) {
                    this._mouseOver = true;
                    this.divWebgl.focus();
                }
                if (this._dragState) {
                    eventsManager.instance.dispatch(Events.DRAG_OVER_WEBGL);
                }
            }

            this.bindedElement.addEventListener("mouseover", this.mouseoverHandler);

            this.mouseupHandler = (event: MouseEvent) => {
                event.stopPropagation();

                this._mouseDownEvent = null;

                if (!this._webglScene) {
                    return;
                }

                if (this._webglScene && this._webglScene.renderer) {
                    if (event.target !== this._webglScene.renderer.domElement && this.zoom === 1) {
                        return;
                    }
                }

                if (!this._mouseOver) {
                    return;
                }

                if (!this.controls) {
                    return;
                }

                switch (event.button) {
                    case 1:
                    case 2:
                        if (this._webglScene.camera && this._webglScene.camera.entity.locked === false) {
                            let command = new Commands.EditRenderCameraCommand(this._webglScene.camera.entity, false);
                            if (this.PlanManager) {
                                this.PlanManager.getInstance().executeCommand(command);
                            } else {
                                command.execute();
                            }
                        }
                        break;
                }

                this.bindedElement.style.cursor = 'default';
                switch (event.button) {
                    case 0:
                        if (this._dragState) {
                            switch (this._dragState.state) {
                                case DRAG_STATE.COATING:
                                    this._webglScene.setCoatingOnDrop((event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                                        (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                                        this._dragState.value, false);
                                    break;
                                case DRAG_STATE.ENTITY:
                                    this._webglScene.setEntityOnDrop((event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                                        (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                                        this._dragState.value);
                                    break;
                            }
                            this._dragState = null;
                        }
                    case 2:
                        this._dragState = null;
                        break;
                }

                let selection = this._webglScene.selection();
                selection = selection.filter((item: any) => {
                    return this._webglScene!.interactionWithItemAllowed(item);
                });
                if (event.ctrlKey && selection.length === 0) {
                    this._webglScene.selectionBox.endPoint.set(
                        (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                        (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                        0.5
                    );
                    this._webglScene.setBoxSelection();
                    return;
                }

                eventsManager.instance.dispatch(MOUSE_EVENTS.UP, {
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1,
                    offsetX: event.offsetX,
                    offsetY: event.offsetY,
                    button: event.button
                });

                this._webglScene.updateEnvs();
                this._webglScene.render();
            }

            this.bindedElement.addEventListener("mouseup", this.mouseupHandler);

            this.wheelHandler = (event: WheelEvent) => {
                event.preventDefault();
                if (!this._webglScene) {
                    return;
                }

                if (!this.controls) {
                    return;
                }

                if (!this.loaded) {
                    return;
                }

                let deltaY = event.deltaY;
                if (!deltaY) {
                    return;
                }

                deltaY /= 100;

                if (event.shiftKey && this._webglScene &&
                    this._webglScene.camera && (this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.PhotoRender ||
                        this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Perspective ||
                        this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Storage)) {
                    this.zoom -= deltaY * 0.1;
                    if (this.zoom < 1) {
                        this.zoom = 1;
                    }
                    this._webglScene.renderer.domElement.setAttribute("style", "transform: scale(" + this.zoom + "); transform-origin: " + this.transformOrigin);
                    this._delegate.render();
                    return;
                }

                // start temporary for camera update
                if (this._webglScene.camera && this._webglScene.camera.entity.locked === false) {
                    this._webglScene.camera.entity.startTemporary();
                }

                this._webglScene.stopEnvUpdate();
                this._webglScene.dollyCamera((deltaY / (event.target as HTMLElement).clientHeight) * 4000, {
                    x: (event.offsetX / (event.target as HTMLElement).clientWidth) * 2 - 1,
                    y: (((event.target as HTMLElement).clientHeight - event.offsetY) / (event.target as HTMLElement).clientHeight) * 2 - 1
                });
                this._webglScene.render();

                // end temporary after 800ms for camera update
                if (this.editRenderCameraCommandTimeout) {
                    clearTimeout(this.editRenderCameraCommandTimeout);
                }
                this.editRenderCameraCommandTimeout = setTimeout(() => {
                    if (this._webglScene && this._webglScene.camera && this._webglScene.camera.entity.locked === false) {
                        let command = new Commands.EditRenderCameraCommand(this._webglScene.camera.entity, false);
                        if (this.PlanManager) {
                            this.PlanManager.getInstance().executeCommand(command);
                        } else {
                            command.execute();
                        }
                    }
                    this.editRenderCameraCommandTimeout = null;
                }, 800);
            }

            this.bindedElement.addEventListener("wheel", this.wheelHandler);

            this.keydownHandler = (event: KeyboardEvent) => {
                this.currentKeyPressed = event.keyCode;
                if (!this._webglScene) {
                    return;
                }

                this._webglScene.keyPressed.shift = event.shiftKey;
                this._webglScene.keyPressed.ctrl = event.ctrlKey;
                if (this.currentKeyPressed === 88) {
                    this._webglScene.keyPressed.x = true;
                }
                if (this.currentKeyPressed === 82) {
                    this._webglScene.keyPressed.r = true;
                }
                if (this.currentKeyPressed === 70) {
                    this._webglScene.keyPressed.f = true;
                }

                if (this.settings.interactiveProject && this._interactiveProjectHandler) {
                    this._interactiveProjectHandler.HandleKeyboard(event);
                }

                if (!this.controls) {
                    return;
                }

                if (!this._mouseOver) {
                    return;
                }

                if (!this.loaded) {
                    return;
                }

                this._webglScene.stopEnvUpdate();

                // Camera shortcuts
                // @ts-ignore
                if ((event.keyCode === 65 || event.keyCode === 80 || event.keyCode === 72 || event.keyCode === 82 || event.keyCode === 86 || event.keyCode === 67) && event.shiftKey) {
                    // @ts-ignore
                    this._changeCamera(event.keyCode);
                }

                // @ts-ignore
                switch (event.keyCode) {
                    case 13:
                        if (this._webglScene.camera && this.PlanManager !== null && this.PlanManager !== undefined) {
                            if (this._webglScene.camera.entity.cameraType === SceneConstants.CameraType.Video && this._videoPreviewIntervalId === null) {
                                const allArrangements = this.PlanManager.getInstance().world.currentScene.arrangementObjects;

                                for (let i = 0; i < allArrangements.length; ++i) {
                                    const entity = this._webglScene.getPlanEntity(allArrangements[i].id);
                                    if (!entity) {
                                        continue;
                                    }

                                    this._webglScene.setLayer(entity.object!, 1);
                                }

                                this._videoPreviewNbUnhiddenObjects = 0;
                                this._videoPreviewCurrentImage = 0;
                                this._videoPreviewIntervalId = setInterval(this._videoPreviewStep, 200);
                            }
                        }
                        break;
                    case 90: //z
                        if (!event.ctrlKey) {
                            if (this._webglScene.camera && this._webglScene.camera.entity.locked === false) {
                                this._webglScene.camera.entity.startTemporary();
                                this._webglScene.targetCamera((this._mouseMoveEvent.offsetX / this._mouseMoveEvent.target.clientWidth) * 2 - 1, ((this._mouseMoveEvent.target.clientHeight - this._mouseMoveEvent.offsetY) / this._mouseMoveEvent.target.clientHeight) * 2 - 1);
                                let command = new Commands.EditRenderCameraCommand(this._webglScene.camera.entity, false);
                                if (this.PlanManager) {
                                    this.PlanManager.getInstance().executeCommand(command);
                                } else {
                                    command.execute();
                                }
                            }
                            else if (!this._webglScene.camera) {
                                // update free camera target
                                this._webglScene.targetCamera((this._mouseMoveEvent.offsetX / this._mouseMoveEvent.target.clientWidth) * 2 - 1, ((this._mouseMoveEvent.target.clientHeight - this._mouseMoveEvent.offsetY) / this._mouseMoveEvent.target.clientHeight) * 2 - 1);
                            }
                        }
                        if (event.ctrlKey) {
                            this._webglScene.undoAction();
                        }
                        break;
                    case 89: //y
                        if (event.ctrlKey) {
                            this._webglScene.redoAction();
                        }
                        break;
                    case 40: //down
                        if (this._bPressed) {
                            const selection = this._webglScene.selection();
                            const newObjectsAndParents: Array<{entity: Entity, parent: Entity}> = [];
                            const oldObjects: Array<Entity> = [];

                            for (let i = 0; i < selection.length; i++) {
                                var selected = selection[i];
                                if (selected instanceof ArrangementGroup) {
                                    if (selected.masterObjectId) {
                                        for (let j = 0; j < selection[i].children.length; j++) {
                                            if ((selected.children[j] as ArrangementObject).objectId === selected.masterObjectId) {
                                                oldObjects.push(selected);
                                                newObjectsAndParents.push({ entity: selected.children[j], parent: selected.parent! });
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                            if (oldObjects.length > 0) {
                                let command = new Commands.ReplaceEntitiesCommand(oldObjects, newObjectsAndParents);
                                if (this.PlanManager) {
                                    this.PlanManager.getInstance().executeCommand(command);
                                } else {
                                    command.execute();
                                }
                            }
                        }
                        else {
                            if (event.ctrlKey) {
                                this._webglScene.snapSelectionDown();
                            }
                            if (this._webglScene.gizmo['enabled']) {
                                if (this._webglScene.gizmoPosition === WebglScene.GizmoPosition.Bottom) {
                                    this._webglScene.gizmoPosition = WebglScene.GizmoPosition.Center;
                                } else {
                                    this._webglScene.gizmoPosition = WebglScene.GizmoPosition.Bottom;
                                }
                                const selection = this._webglScene.selection();
                                this._webglScene.setGizmoPosition(selection, true);
                            }
                        }
                        break;
                    case 38: //up
                        if (event.ctrlKey) {
                            this._webglScene.snapSelectionUp();
                        }
                        if (this._webglScene.gizmo['enabled']) {
                            if (this._webglScene.gizmoPosition === WebglScene.GizmoPosition.Top) {
                                this._webglScene.gizmoPosition = WebglScene.GizmoPosition.Center;
                            } else {
                                this._webglScene.gizmoPosition = WebglScene.GizmoPosition.Top;
                            }
                            const selection = this._webglScene.selection();
                            this._webglScene.setGizmoPosition(selection, true);
                        }
                        break;
                    case 68: //d
                        if (event.ctrlKey && event.altKey) {
                            if (this.PlanManager) {
                                eventsManager.instance.dispatch(Events.CLEAN_WORLD_TEMPLATE);
                            }
                        }
                        else {
                            if (!event.ctrlKey && !event.altKey) {
                                this._webglScene.setCameraDof((this._mouseMoveEvent.offsetX / this._mouseMoveEvent.target.clientWidth) * 2 - 1, ((this._mouseMoveEvent.target.clientHeight - this._mouseMoveEvent.offsetY) / this._mouseMoveEvent.target.clientHeight) * 2 - 1);
                                if (this._webglScene.camera) {
                                    let command = new Commands.EditRenderCameraCommand(this._webglScene.camera.entity, true);
                                    if (this.PlanManager) {
                                        this.PlanManager.getInstance().executeCommand(command);
                                    } else {
                                        command.execute();
                                    }
                                }
                            }
                        }
                        break;
                    case 39: //right
                        if (this._bPressed) {
                            this.SwitchPopulate(1);
                        }
                        else {
                            if (this._vPressed) {
                                this.SwitchCloseObject(1);
                            }
                            else {
                                if (this._webglScene.gizmo['enabled']) {
                                    if (this._webglScene.gizmoPosition === WebglScene.GizmoPosition.Center || this._webglScene.gizmoPosition === WebglScene.GizmoPosition.xLeft || this._webglScene.gizmoPosition === WebglScene.GizmoPosition.yLeft) {
                                        this._webglScene.gizmoPosition = WebglScene.GizmoPosition.xRight;
                                    } else if (this._webglScene.gizmoPosition === WebglScene.GizmoPosition.xRight) {
                                        this._webglScene.gizmoPosition = WebglScene.GizmoPosition.yRight;
                                    } else {
                                        this._webglScene.gizmoPosition = WebglScene.GizmoPosition.Center;
                                    }
                                    const selection = this._webglScene.selection();
                                    this._webglScene.setGizmoPosition(selection, true);
                                }
                            }
                        }
                        break;
                    case 37: //left
                        if (this._bPressed) {
                            this.SwitchPopulate(-1);
                        }
                        else {
                            if (this._vPressed) {
                                this.SwitchCloseObject(-1);
                            }
                            else {
                                if (this._webglScene.gizmo['enabled']) {
                                    if (this._webglScene.gizmoPosition === WebglScene.GizmoPosition.Center || this._webglScene.gizmoPosition === WebglScene.GizmoPosition.xRight || this._webglScene.gizmoPosition === WebglScene.GizmoPosition.yRight) {
                                        this._webglScene.gizmoPosition = WebglScene.GizmoPosition.xLeft;
                                    } else if (this._webglScene.gizmoPosition === WebglScene.GizmoPosition.xLeft) {
                                        this._webglScene.gizmoPosition = WebglScene.GizmoPosition.yLeft;
                                    } else {
                                        this._webglScene.gizmoPosition = WebglScene.GizmoPosition.Center;
                                    }
                                    const selection = this._webglScene.selection();
                                    this._webglScene.setGizmoPosition(selection, true);
                                }
                            }
                        }
                        break;
                    case 46: //delete
                        this._webglScene.deleteSelectedEntities();
                        break;
                    case 83: //s
                        this._webglScene.toggleSnap();
                        break;
                    case 82: //r
                        if (!this._dragState) {
                            if (event.shiftKey) {
                                this._webglScene.duplicateEntitiesOffset();
                            }
                            else if (this._webglScene.gizmo.mode === 'scale' && this._webglScene.gizmo['enabled']) {
                                this._webglScene.gizmo['enabled'] = false;
                                this._webglScene.gizmo.visible = false;
                                this._webglScene.gizmo.space = 'world';
                            } else {
                                this._webglScene.gizmo.setMode("scale");
                                this._webglScene.gizmo['enabled'] = true;
                                this._webglScene.gizmo.visible = true;
                                this._webglScene.gizmo.space = 'local';
                            }
                            this._delegate.render();
                        }
                        break;
                    case 70: //f
                        if (this._loadedEntity) {
                            if (Array.isArray(this._loadedEntity)) {
                                for (let i = 0; i < this._loadedEntity.length; ++i) {
                                    this._webglScene.fitCamera(this._loadedEntity[i], i);
                                }
                            } else {
                                this._webglScene.fitCamera(this._loadedEntity, 0);
                            }
                        }
                        break;
                    case 48: // zero
                    case 96: // zero numpad
                        this._webglScene.resetCameraPitch();
                        break;
                    case 71: // g
                        if (this.PlanManager && this.PlanManager.getInstance().selectedEntities.length > 1) {
                            eventsManager.instance.dispatch(Events.GROUP_ENTITIES);
                        } else {
                            this.thirdGrid = !this.thirdGrid;
                        }
                        this._delegate.render();
                        break;
                    case 73: // i
                        {
                            let selection = this._webglScene.selection();
                            if (selection.length > 1) {
                                let command = new Commands.SnapEntitiesCommand(selection, Commands.SnapEntitiesCommand.SnapEnum.CenterOnTop);
                                if (this.PlanManager) {
                                    this.PlanManager.getInstance().executeCommand(command);
                                } else {
                                    command.execute();
                                }
                            }
                        }
                        break;
                    case 81: // q
                        const value = parseInt(SavaneCookie.getCookie("Rhinov-DesignerFilter-ObjectFilterEnabled", "" + 0));

                        if (value === 0) {
                            SavaneCookie.setCookie("Rhinov-DesignerFilter-ObjectFilterEnabled", "" + 1);
                        }
                        else {
                            SavaneCookie.setCookie("Rhinov-DesignerFilter-ObjectFilterEnabled", "" + 0);
                        }
                        eventsManager.instance.dispatch(Events.ENABLE_OBJECT_FILTERS_CHANGED, { forceUpdate: true });
                        break;
                    case 80: // p
                        if (!event.shiftKey && this._webglScene.camera) {
                            this._webglScene.toggleFreeCamera();
                            this._cameraState = CAMERA_STATES.FREE;
                            this._delegate.render();
                        }
                        break;
                    case 86: // v
                        if (!event.shiftKey && !event.ctrlKey) {
                            this._vPressed = true;
                        }
                        if (event.ctrlKey) {
                            this._webglScene.pasteCoating((this._mouseMoveEvent.offsetX / this._mouseMoveEvent.target.clientWidth) * 2 - 1, ((this._mouseMoveEvent.target.clientHeight - this._mouseMoveEvent.offsetY) / this._mouseMoveEvent.target.clientHeight) * 2 - 1);
                        }
                        break;
                    case 66: // b
                        this._bPressed = true;
                        break;
                    case 84: //t
                        cocosPosition.z = this._webglScene.getDefaultCameraDistanceFit();
                        this._webglScene.defaultCamera.position.copy(cocosPosition);
                        (this._webglScene.defaultCamera as any).target = new THREE.Vector3(cocosPosition.x, cocosPosition.y, 0);
                        this._webglScene.defaultCamera.up = new THREE.Vector3(0, 1, 0);
                        this._webglScene.defaultCamera.lookAt((this._webglScene.defaultCamera as any).target);
                        this._webglScene.defaultCameraMoved = false;

                        this._webglScene.updateCamera(null);
                        this._cameraState = CAMERA_STATES.FREE;
                        this._delegate.render();
                        break;
                    case 67: //c
                        if (event.ctrlKey) {
                            this._webglScene.copyCoating((this._mouseMoveEvent.offsetX / this._mouseMoveEvent.target.clientWidth) * 2 - 1, ((this._mouseMoveEvent.target.clientHeight - this._mouseMoveEvent.offsetY) / this._mouseMoveEvent.target.clientHeight) * 2 - 1);
                        } else {
                            if (!event.shiftKey) {
                                if (this._enable_additionals_afters) {
                                    this._webglScene.clearExcludedObject();
                                    this._webglScene.createCameraFromFreeCamera(this.deliverable);
                                }
                            }
                        }
                        break;
                    case 65: //a
                        if (!event.shiftKey && !event.ctrlKey) {
                            this._webglScene.toggleMagnet();
                        }
                        if (event.ctrlKey) {
                            if (this.PlanManager) {
                                this.PlanManager.getInstance().selectAll();
                            }
                        }
                        break;
                    case 85: //u
                    if (this.PlanManager) {
                            eventsManager.instance.dispatch(Events.UNGROUP_ENTITIES);
                        }
                        break;
                    case 32: //space
                        {
                            const selection = this._webglScene.selection();
                            let doUpdateHull = true;
                            for (let i = 0; i < selection.length; ++i) {
                                const entity = selection[i];
                                if (entity.isArrangementObjectEntity() || entity.isArrangementGroupEntity()) {
                                    doUpdateHull = false;
                                    entity.locked = !entity.locked;
                                }
                            }

                            if (doUpdateHull) {
                                this._updateHulls();
                            }
                        }
                        break;
                    case 9: //tab
                        this._changeGizmoSpace(event);
                        this._delegate.render();
                        break;
                    case 87: //w
                        if (event.ctrlKey && event.altKey) {
                            const selection = this._webglScene.selection();
                            this._webglScene.detachSelection();
                            for (let i = 0; i < selection.length; ++i) {
                                const entity = selection[i];
                                if (entity instanceof ArrangementObject && entity.locked !== true && entity.stretchability && entity.excludeFromShoppingList === true) {
                                    entity.disableStretchability();
                                    eventsManager.instance.dispatch(Events.UNABLE_STETCHABILITY_ENTITY,
                                            {
                                                entity: entity,
                                                active: false
                                            });

                                    const webglEntity = this._webglScene.getPlanEntity(entity.id);
                                    if (webglEntity) {
                                        webglEntity.update();
                                    }
                                }
                            }
                            this._webglScene.attachSelection();
                        } else {
                            if (this._webglScene.gizmo.mode === 'translate' && this._webglScene.gizmo['enabled']) {
                                this._webglScene.gizmo['enabled'] = false;
                                this._webglScene.gizmo.visible = false;
                            } else {
                                this._webglScene.gizmo.setMode("translate");
                                this._webglScene.gizmo['enabled'] = true;
                                this._webglScene.gizmo.visible = true;
                            }
                            this._delegate.render();
                        }
                        break;
                    case 88: //x
                        if (event.ctrlKey && event.altKey) {
                            const selection = this._webglScene.selection();
                            this._webglScene.detachSelection();
                            for (let i = 0; i < selection.length; ++i) {
                                const entity = selection[i];
                                if (entity instanceof ArrangementObject && entity.locked !== true && entity.stretchability === undefined) {
                                    entity.enableStretchability();
                                    eventsManager.instance.dispatch(Events.UNABLE_STETCHABILITY_ENTITY,
                                            {
                                                entity: entity,
                                                active: true
                                            });

                                    const webglEntity = this._webglScene.getPlanEntity(entity.id);
                                    if (webglEntity) {
                                        webglEntity.update();
                                    }
                                }
                            }
                            this._webglScene.attachSelection();
                        }
                        break;
                    case 69: //e
                        if (this._webglScene.gizmo.mode === 'rotate' && this._webglScene.gizmo['enabled']) {
                            this._webglScene.gizmo['enabled'] = false;
                            this._webglScene.gizmo.visible = false;
                        } else {
                            this._webglScene.gizmo.setMode("rotate");
                            this._webglScene.gizmo['enabled'] = true;
                            this._webglScene.gizmo.visible = true;
                        }
                        this._delegate.render();
                        break;
                    case 79: //o
                        // Toggle Holdout
                        {
                            const selection = this._webglScene.selection();
                            for (let i = 0; i < selection.length; ++i) {
                                let entity = selection[i];
                                entity.holdout = !entity.holdout;
                                if (this._webglScene) {
                                    this._webglScene.updateHull(entity);
                                }
                            }
                        }
                        break;
                    case 112: //F1
                        if (this.PlanManager) {
                            this.PlanManager.getInstance()._hideArrangements = !this.PlanManager.getInstance()._hideArrangements;
                            eventsManager.instance.dispatch(Events.HIDE_ARRANGEMENTS);
                        }
                        event.preventDefault();
                        break;
                    case 113: //F2
                        if (this.PlanManager) {
                            this.PlanManager.getInstance()._hideAxo = !this.PlanManager.getInstance()._hideAxo;
                            eventsManager.instance.dispatch(Events.HIDE_AXO);
                        }
                        break;
                    case 114: //F3
                        if (this._webglScene) {
                            this._webglScene.hullTransparency = !this._webglScene.hullTransparency;
                            this._webglScene.render();
                        }
                        event.preventDefault();
                        break;
                    case 115: //F4
                        this._togglePhysics(event);
                        this._delegate.render();
                        break;
                    case 120: //F9
                        if (this._webglScene) {
                            this._webglScene.renderPass.enabled = !this._webglScene.renderPass.enabled;
                            this._webglScene.outlinePass.enabled = !this._webglScene.outlinePass.enabled;
                            this._webglScene.composer.reset();
                        }
                        break;
                    case 121: //F10
                        if (this._webglScene) {
                            const strMime = "image/png";
                            const strDownloadMime = "image/octet-stream";

                            const imgData = this._webglScene.renderer.domElement.toDataURL(strMime);
                            const data = imgData.replace(strMime, strDownloadMime)
                            const link = document.createElement('a');
                            document.body.appendChild(link); //Firefox requires the link to be in the body
                            link.download = "capture.png";
                            link.href = data;
                            link.click();
                            document.body.removeChild(link);
                        }
                        break;
                    case 72: //h
                        if (this._webglScene && !event.shiftKey) {
                            this._webglScene.displayHeight = !this._webglScene.displayHeight;
                            this._delegate.render();
                        }
                        break;
                    case 75: //k
                        break;
                    case 123: //F12
                        if (this._webglScene) {
                            this._webglScene.stats.showPanel(0);
                        }
                        break;
                    case 187: // +
                        if (this.photoOpacity < 0.8) {
                            this.photoOpacity += 0.1;
                            this._delegate.render();
                        }
                        break;
                    case 219: // -
                        if (this.photoOpacity > 0.2) {
                            this.photoOpacity -= 0.1;
                            this._delegate.render();
                        }
                        break;
                    default:
                        event.preventDefault();
                }

                //number 1 -> 9
                // @ts-ignore
                if (event.keyCode >= 49 && event.keyCode <= 57) {
                    if (this.displayPhoto) {
                        setTimeout(() => {
                            this.displayPhoto = false;
                            this._delegate.render();
                        });
                    } else {
                        // @ts-ignore
                        this._setCurrentPhoto(event.keyCode - 49);
                    }
                }

                this._webglScene.render();
            }

            this.keyupHandler = (event: KeyboardEvent) => {
                this.currentKeyPressed = null;
                if (!this._webglScene) {
                    return;
                }

                if (!this.loaded) {
                    return;
                }

                this._webglScene.keyPressed.shift = event.shiftKey;
                this._webglScene.keyPressed.ctrl = event.ctrlKey;

                // @ts-ignore
                switch (event.keyCode) {
                    case 88:
                        this._webglScene.keyPressed.x = false;
                        break;
                    case 82:
                        this._webglScene.keyPressed.r = false;
                        break;
                    case 70:
                        this._webglScene.keyPressed.f = false;
                        break;
                    case 66:
                        this._bPressed = false;
                        break;
                    case 86:
                        this._vPressed = false;
                        break;
                }

                if (!this.controls) {
                    return;
                }

                if (!this._mouseOver) {
                    return;
                }

                this._webglScene.updateEnvs();
            }

            document.addEventListener("keydown", this.keydownHandler);
            document.addEventListener("keyup", this.keyupHandler);

            this.initialize();

            window.addEventListener('resize', this._resize);

            let destroyObserver = new MutationObserver((mutationsList: MutationRecord[], _observer: MutationObserver) => {
                mutationsList.forEach((mutation) => {
                    if (mutation.type === 'childList' && Array.from(mutation.removedNodes).includes(this.bindedElement)) {
                        this.destroy();
                    }
                });
            });

            let observerConfig = { childList: true };

            destroyObserver.observe(document.body, observerConfig);

            if (callback) {
                callback();
            }

        });

    }

    public SwitchCloseObject(way: number) {
        const selection = this._webglScene!.selection();
        const newObjectsAndParents: any[] = [];
        const oldObjects: any[] = [];

        this._displayWarning = false;

        async.eachSeries(selection, (entity: ArrangementObject, endCallback: any) => {
            if (entity.isArrangementObjectEntity()) {
                if ((entity as any).closeObjects === undefined) {
                    this._loadCloseObjects(entity, way, oldObjects, newObjectsAndParents, () => {
                        endCallback();
                    });
                }
                else {
                    this._switchCloseObjects(entity, way, oldObjects, newObjectsAndParents, () => {
                        endCallback();
                    });
                }
            } else {
                endCallback();
            }
        },
            (_err: any) => {
                if (oldObjects.length > 0) {
                    let command = new Commands.ReplaceEntitiesCommand(oldObjects, newObjectsAndParents);
                    if (this.PlanManager) {
                        this.PlanManager.getInstance().executeCommand(command);
                    } else {
                        command.execute();
                    }
                }

                if (this._displayWarning) {
                    eventsManager.instance.dispatch(Events.DISPLAY_TOASTER, { message: 'L\'opération demandée n\'a pas pu être effectuée sur au moins un des objets sélectionnés.' });
                }
            });
    }

    public SwitchPopulate(way: number, callback?: CallableFunction) {
        const selection = this._webglScene!.selection();
        const newObjectsAndParents: any[] = [];
        const oldObjects: any[] = [];

        async.eachSeries(selection, (entity: ArrangementObject, endCallback: CallableFunction) => {
            if (entity.isArrangementObjectEntity()) {
                loadPopulatedObjects(entity, oldObjects, newObjectsAndParents, null, "b2c,b2b,content_factory", (displayWarning: boolean) => {
                    this._displayWarning = displayWarning;
                    endCallback();
                });
            }
            else if (entity.isArrangementGroupEntity()) {
                switchPopulatedObjects(entity, way, oldObjects, newObjectsAndParents, (displayWarning: boolean) => {
                    this._displayWarning = displayWarning;
                    endCallback();
                });
            } else {
                endCallback();
            }
        },
            () => {
                if (oldObjects.length > 0) {
                    let command = new Commands.ReplaceEntitiesCommand(oldObjects, newObjectsAndParents);
                    if (this.PlanManager) {
                        this.PlanManager.getInstance().executeCommand(command);
                    } else if (callback) {
                        callback(command, this._displayWarning);
                    } else {
                        command.execute();
                    }
                } else if (callback) {
                    callback(null, this._displayWarning);
                }

                if (this._displayWarning) {
                    eventsManager.instance.dispatch(Events.DISPLAY_TOASTER, { message: 'L\'opération demandée n\'a pas pu être effectuée sur au moins un des objets sélectionnés.' });
                }
            });
    }
}
